<template>
  <div class="chat-page">

    <!-- Paylaşılan sohbet banner -->
    <div v-if="sharedMeta" class="shared-banner">
        <div v-if="sharedLoading">
            Paylaşılan sohbet yükleniyor...
        </div>
        <div v-else-if="sharedError">
            <span>{{ sharedError }}</span>
            <button class="shared-banner-btn" @click="checkSharedConversationFromRoute">
                Tekrar dene
            </button>
        </div>
        <div v-else>
            <strong>Paylaşılan sohbeti görüntülüyorsunuz.</strong>
            <span v-if="sharedMeta?.title"> — {{ sharedMeta.title }}</span>
        
        </div>
    </div>
    <!-- MESSAGES AREA -->
    <main
      class="chat-body"
      ref="messageContainer"
      @scroll.passive="handleScroll"
      :class="{ 'is-empty': !chatStarted }"
    >
      <div class="chat-inner">

        <!-- HERO: sadece hiç user mesajı yokken -->
        <div v-if="!chatStarted" class="chat-hero">
          <div class="hero-eyebrow">KKTC Hukuk Asistanı</div>
          <h1 class="chat-title">
            Merhaba {{ displayName }}, bugün hangi konuyu araştırıyorsunuz?
          </h1>
          <p class="hero-hint">
            Yasa maddesi sorun, emsal karar arayın veya bir tarafın davalarını listeleyin —
            her cevap kaynak belgelerine dayanır.
          </p>

          <!-- ✅ INPUT: ilk ekranda hero içinde, sticky değil -->
          <div class="hero-input">
            <ChatComposer
              v-model="userInput"
              :loading="loading"
              :selected-model="selectedModel"
              :model-options="modelOptions"
              @send="sendMessage"
              @stop="stopStreaming"
              @select-model="selectModel"
            />
          </div>

          <!-- Örnek soru kalıpları: gerçek kullanım desenlerinden -->
          <div class="hero-samples" aria-label="Örnek sorular">
            <button
              v-for="s in heroSamples"
              :key="s"
              type="button"
              class="hero-sample-chip"
              @click="askSuggestion(s)"
            >{{ s }}</button>
          </div>
        </div>

        <!-- CHAT MESSAGES -->
        <div v-else class="messages">
          <div
            v-for="(msg, idx) in messages"
            v-show="!(msg.isStreaming && !msg.text)"
            :key="msg.id || idx"
            :class="['msg-row', msg.sender === 'user' ? 'is-user' : 'is-assistant']"
          >
            <div class="msg-bubble">
                <!-- ✅ ACTIONS: copy -->
                <div class="msg-actions">
                    <!-- COPY -->
                    <button
                    class="msg-action-btn"
                    type="button"
                    @click.stop="copyMessage(msg.text)"
                    :title="copiedMessageIndex === idx ? 'Kopyalandı' : 'Kopyala'"
                    aria-label="Kopyala"
                    >
                    <span class="material-symbols-outlined">
                        {{ copiedMessageIndex === idx ? 'check' : 'content_copy' }}
                    </span>
                    </button>
                </div>

                <div class="msg-text" v-html="msg.text"></div>

                <!-- Router rozeti: 'auto' seçiliyken hangi model cevapladı -->
                <div v-if="msg.routedModel" class="routed-badge">
                    <span class="material-symbols-outlined">bolt</span>
                    <span>{{ msg.routedModel }}</span>
                </div>

                <!-- ⚠ Doğrulanamayan atıf uyarısı (doğrulama mührü künye kartlarında) -->
                <div
                    v-if="msg.sender === 'assistant' && msg.citations && msg.citations.unverified && msg.citations.unverified.length"
                    class="citation-warn-line"
                >
                    <span class="material-symbols-outlined">warning</span>
                    <span>Doğrulanamayan atıf: {{ msg.citations.unverified.join(", ") }} — lütfen kaynaktan teyit edin</span>
                </div>

                <!-- 📜 DAYANAK BELGELER — Karar Künyesi kartları -->
                <div v-if="msg.sender === 'assistant' && msg.sources && msg.sources.length > 0" class="msg-sources">
                    <div class="sources-header">Dayanak Belgeler</div>

                    <div
                        v-for="grp in sourceGroups(msg.sources)"
                        :key="grp.key"
                        class="sources-group"
                    >
                        <div class="sources-subheader">{{ grp.title }}</div>
                        <div class="kunye-list">
                            <div
                                class="kunye-card"
                                v-for="src in grp.items"
                                :key="grp.key + '-' + src.file_name"
                                :class="{ 'is-unavailable': src.available === false }"
                            >
                                <div
                                    v-if="sealState(msg, src)"
                                    class="kunye-seal"
                                    :class="'seal-' + sealState(msg, src)"
                                    :title="sealState(msg, src) === 'ok' ? 'Atıf kaynaklarda doğrulandı' : 'Bu atıf doğrulanamadı — kaynaktan teyit edin'"
                                >
                                    <span class="material-symbols-outlined">{{ sealState(msg, src) === 'ok' ? 'verified' : 'question_mark' }}</span>
                                </div>
                                <div class="kunye-info">
                                    <div class="kunye-court">{{ kunyeCourt(src) || grp.title }}</div>
                                    <div v-if="kunyeNumbers(src)" class="kunye-numbers">{{ kunyeNumbers(src) }}</div>
                                    <div class="kunye-name" :title="sourceDisplayName(src)">
                                        {{ sourceDisplayName(src) }}
                                        <span v-if="src.chunk_count && src.chunk_count > 1" class="source-badge">{{ src.chunk_count }} bölüm</span>
                                    </div>
                                </div>
                                <div class="kunye-actions">
                                    <button
                                        class="source-btn"
                                        @click.stop="openDocumentPreview(src)"
                                        :title="src.available === false ? 'Dosya yerelde bulunamadı' : 'Belgeyi önizle'"
                                        :disabled="src.available === false"
                                    >
                                        <span class="material-symbols-outlined">visibility</span>
                                    </button>
                                    <button
                                        class="source-btn"
                                        @click.stop="downloadSourceFile(src)"
                                        :title="src.available === false ? 'Dosya yerelde bulunamadı' : 'Belgeyi indir'"
                                        :disabled="downloadingSourceFile === src.file_name"
                                    >
                                        <span class="material-symbols-outlined">
                                            {{ downloadingSourceFile === src.file_name ? 'hourglass_empty' : 'download' }}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ⭐ STAR RATING: cevap altında, paylaşılan görünümde gizli -->
                <div v-if="msg.sender === 'assistant' && !sharedMeta && !msg.isStreaming && !msg.isWelcome" class="msg-rating-area" :key="'rating-' + (msg.questionId ?? msg.timestamp) + '-' + (glowCounter[msg.questionId ?? String(msg.timestamp)] || 0)" :class="{ 'rating-glow': shouldGlow(msg) }">
                  <div v-if="!msg.rating" class="rating-prompt-label">Bu yanıt faydalı oldu mu?</div>
                  <div class="msg-rating">
                    <button
                      v-for="i in 5"
                      :key="i"
                      class="rating-star-btn"
                      :class="{ filled: i <= (hoverRating[msg.id ?? msg.questionId] || msg.rating || 0) }"
                      type="button"
                      :aria-label="`${i} yıldız`"
                      :title="`${i} yıldız`"
                      @mouseenter="hoverRating[msg.id ?? msg.questionId] = i"
                      @mouseleave="hoverRating[msg.id ?? msg.questionId] = 0"
                      @click.stop="sendRating(msg, i)"
                    >
                      <span class="material-symbols-outlined">
                        {{ i <= (hoverRating[msg.id ?? msg.questionId] || msg.rating || 0) ? 'star' : 'star_outline' }}
                      </span>
                    </button>
                    <span v-if="msg.comment" class="rating-has-comment" title="Yorum var">
                      <span class="material-symbols-outlined">chat_bubble</span>
                    </span>
                  </div>

                  <!-- Yorum kutusu -->
                  <div v-if="msg.rating && commentOpen[msg.id ?? msg.questionId]" class="rating-comment-box">
                    <textarea
                      v-model="commentText[msg.id ?? msg.questionId]"
                      class="rating-comment-input"
                      placeholder="Yorum ekle (isteğe bağlı)..."
                      rows="2"
                      @keydown.enter.prevent="sendComment(msg)"
                    />
                    <div class="rating-comment-actions">
                      <button class="rating-comment-btn primary" @click.stop="sendComment(msg)">Gönder</button>
                      <button class="rating-comment-btn" @click.stop="commentOpen[msg.id ?? msg.questionId] = false">Vazgeç</button>
                    </div>
                  </div>
                  <div v-else-if="msg.rating && msg.comment" class="rating-comment-preview" @click.stop="openComment(msg)">
                    <span class="material-symbols-outlined" style="font-size:14px">edit</span>
                    {{ msg.comment }}
                  </div>
                </div>

                <div class="msg-meta">
                    <span class="msg-time">{{ formatTime(msg.timestamp) }}</span>
                </div>
                </div>


          </div>

          <div v-if="loading" class="msg-row is-assistant" role="status" aria-live="polite">
            <div class="msg-bubble loading-logo-bubble">
              <div class="loading-scales">
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" class="scales-svg">
                  <!-- direk -->
                  <line x1="22" y1="4" x2="22" y2="36" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  <!-- kol (sallanıyor) -->
                  <g class="scales-beam">
                    <line x1="6" y1="14" x2="38" y2="14" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                    <!-- sol kefecik -->
                    <line x1="9"  y1="14" x2="9"  y2="22" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                    <path d="M5 22 Q9 25 13 22"  stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/>
                    <!-- sağ kefecik -->
                    <line x1="35" y1="14" x2="35" y2="22" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                    <path d="M31 22 Q35 25 39 22" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" fill="none"/>
                  </g>
                  <!-- taban -->
                  <path d="M15 36 H29" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  <!-- merkez top -->
                  <circle cx="22" cy="14" r="2.5" fill="currentColor"/>
                </svg>
              </div>
              <div class="loading-logo-text">{{ loadingStatusText }}</div>
            </div>
          </div>

          <div ref="bottomAnchor" class="bottom-anchor"></div>
        </div>

      </div>
      <!-- ✅ Scroll to bottom (ChatGPT gibi) -->
        

    </main>

    <button
        v-if="messages.length > 1 && showScrollButton"
        class="scroll-down-btn"
        type="button"
        @click="onScrollToBottomClick"
        aria-label="En alta in"
        title="En alta in"
        >
        <span class="material-symbols-outlined">south</span>
    </button>

    <!-- Kullanım Kılavuzu butonu -->
    <button
        class="guide-help-btn"
        type="button"
        @click="guideVisible = true"
        aria-label="Kullanım Kılavuzu"
        title="Kullanım Kılavuzu"
    >
        <span class="material-symbols-outlined">help_outline</span>
    </button>

    <!-- Kullanım Kılavuzu Paneli -->
    <GuidePanel v-model="guideVisible" @dismiss-forever="onGuideDismissForever" />

    <!-- ✅ INPUT: chat başladıysa altta fixed + sticky hissi -->
    <footer v-if="chatStarted" class="chat-input-bar is-sticky">
      <div class="input-inner">
        <ChatComposer
          v-model="userInput"
          :loading="loading"
          :selected-model="selectedModel"
          :model-options="modelOptions"
          @send="sendMessage"
          @stop="stopStreaming"
          @select-model="selectModel"
        />
        <p class="disclaimer">
          MahAi hata yapabilir. Lütfen cevapların doğruluğunu teyid ediniz.
          <button type="button" class="disclaimer-link" @click="principlesVisible = true">Yapay Zeka Kullanım İlkeleri için tıklayınız.</button>
        </p>
      </div>
    </footer>

    <!-- AI PRINCIPLES MODAL -->
    <div v-if="principlesVisible" class="decisions-ai-modal-backdrop" @click.self="principlesVisible = false">
      <div class="decisions-ai-modal principles-modal" role="dialog" aria-modal="true" aria-label="Yapay Zeka Kullanım İlkeleri">
        <div class="decisions-ai-modal-header">
          <span class="material-symbols-outlined" style="color:var(--sage-green);font-size:22px;">gavel</span>
          <h3>Yapay Zeka Kullanım İlkeleri Kılavuzu</h3>
          <button class="decisions-ai-modal-close" @click="principlesVisible = false">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>
        <div class="decisions-ai-modal-body principles-modal-body">
          <p class="principles-intro">
            Bu kılavuz yargıçların görevleri icabı veya yargısal herhangi bir işlemde yapay zeka araçlarının
            kullanımı konusunda uyması gereken ilkeleri düzenlemektedir.
          </p>
          <ol class="principles-list">
            <li>Mahkemeler tarafından kullanıma sunulsun veya sunulmasın, herhangi bir yapay zeka aracı kullanılmadan önce, kullanılacak yapay zeka aracının yetenekler ve potansiyel sınırlamaları hakkında temel bilgiye sahip olunduğundan emin olunmalıdır.</li>
            <li>Yapay zeka araçları sorduğunuz her soruyu, girdiğiniz her türlü bilgiyi hatırlamaktadır. Bunlar daha sonra başka kullanıcıların sorularına yanıt vermek için kullanılabilmektedir.</li>
            <li>Yapay zeka araçlarının yargısal işlemler ile alakalı kullanılacağı durumlarda, siber güvenliğin sağlandığından emin olunmalıdır. Bu maksatla yapay zekanın yargısal işlemler ile ilgili olarak kullanılacağı durumlarda yapay zeka araçlarına Mahkeme ortamında sağlanan ağ hizmetlerinden erişilmesi en doğru yaklaşım olacaktır.</li>
            <li>Bir yapay zeka aracı tarafından size sağlanan herhangi bir bilginin doğruluğu, bu bilgi kullanılmadan veya bu bilgiye güvenilmeden önce kontrol edilmelidir.</li>
            <li>Yapay zeka araçları tarafından sağlanan bilgiler, yanlış, eksik, yanıltıcı olabilir veya güncel olmayabilir. Yapay zeka tarafından üretilen cevap veya materyal Kuzey Kıbrıs Türk Cumhuriyeti'nde yürürlükte olan mevzuatı kapsadığını belirtse bile, bu doğru olmayabilir. Buna yapay zeka aracının halüsinasyon görmesi sonucu ortaya çıkmış olabilecek kaynak ve alıntılar da dahildir.</li>
            <li>Yapay zeka araçları tarafından üretilen tüm içerikler bağımsız kaynaklardan doğrulanmadan kullanılmamalıdır.</li>
            <li>
              Yapay zeka araçları halüsinasyon görebilirler. Yapay zeka araçlarının halüsinasyon görmesi aşağıdaki formlarda olabilir;
              <ul>
                <li>hayali davalar, alıntılar veya sözler uydurmak ya da var olmayan mevzuat, makaleler veya hukuki metinlere atıfta bulunmak,</li>
                <li>yasa veya yasanın nasıl uygulanabileceği konusunda yanlış ya da yanıltıcı bilgiler vermek, ve</li>
                <li>gerçeğe aykırı olgusal hatalar yapmak.</li>
              </ul>
            </li>
            <li>Yargıçlar her zaman huzurlarındaki yargısal işlem ile ilgili layiha, doküman veya sair yargısal işlem ile ilgili belgeyi okuyup değerlendirmelidirler. Yapay zeka araçları yardımcı olabilir, ancak şahadetin ve olgusal değerlendirmede doğrudan ilgilenmenin yerini almazlar.</li>
            <li>Yapay zeka araçlarının avukatlar veya avukat tarafından temsil edilmeyen kişiler tarafından da kullanılabileceği unutulmamalıdır. Yapay zeka tarafından materyallerde insan gözünün göremeyeceği ancak bilgisayar sistemleri tarafından algılanabilen gizli komut ya da saklı materyallerin yerleştirilmesi ile oluşan "beyaz metin" olarak adlandırılan sahte içeriklerin de bulunabileceğinden ötürü yargısal işlemler sırasında Mahkemenin dikkatine getirilen metin ve/veya dokümanların doğruluğu teyit edilmelidir.</li>
          </ol>
          <div class="principles-signature">
            <span class="material-symbols-outlined">account_balance</span>
            <span>YÜKSEK MAHKEME</span>
          </div>
        </div>
      </div>
    </div>

    <!-- DELETE MODAL -->
    <div v-if="pendingClearChatItem" class="decisions-ai-modal-backdrop" @click.self="cancelDelete">
        <div class="decisions-ai-modal" role="dialog" aria-modal="true" aria-label="Sohbeti Temizle">
        <div class="decisions-ai-modal-title">Sohbeti Temizle</div>
        <div class="decisions-ai-modal-text">
            Bu sohbet içeriğini temizlemek istediğinizden emin misiniz?
        </div>
        <div class="decisions-ai-modal-actions">
            <button class="decisions-ai-modal-btn ghost" type="button" @click="cancelDelete">
            Vazgeç
            </button>
            <button class="decisions-ai-modal-btn danger" type="button" @click="clearChat">
            Temizle
            </button>
        </div>
        </div>
    </div>

    <!-- SHARE LINK MODAL -->
    <div v-if="shareDialogVisible" class="decisions-ai-modal-backdrop" @click.self="shareDialogVisible = false">
    <div class="decisions-ai-modal" role="dialog" aria-modal="true" aria-label="Sohbet Paylaşım Linki">
        <div class="decisions-ai-modal-title">Sohbet Paylaşım Linki</div>
        <div class="decisions-ai-modal-text">
        Bu linki kopyalayıp dilediğiniz yerde paylaşabilirsiniz.
        </div>

        <div style="font-size:12px; background:#f8fafc; border-radius:10px; padding:8px 10px; word-break:break-all; margin-bottom:10px;">
        {{ shareUrl }}
        </div>

        <div class="decisions-ai-modal-actions">
        <button
            class="decisions-ai-modal-btn ghost"
            type="button"
            @click="shareDialogVisible = false"
        >
            Kapat
        </button>
        <button
            class="decisions-ai-modal-btn"
            type="button"
            style="background:#111827;color:#fff;"
            @click="copyShareUrl"
        >
            Kopyala
        </button>
        </div>
    </div>
    </div>

    <!-- Emsal Karar Arama Modal -->
    <PrecedentSearchModal
      v-model="precedentSearchVisible"
      @add-to-chat="addPrecedentToChat"
    />

    <!-- Döküman Önizleme Modal -->
    <DocumentPreviewModal
      v-model="documentPreviewVisible"
      :document="selectedDocument"
      @add-to-chat="handleDocumentAddToChat"
    />

  </div>
</template>


<script>
import axios from "axios";
import { useToast } from "vue-toastification";
import { marked } from "marked";
import PrecedentSearchModal from "./PrecedentSearchModal.vue";
import DocumentPreviewModal from "./DocumentPreviewModal.vue";
import GuidePanel from "./GuidePanel.vue";
import ChatComposer from "./ChatComposer.vue";
import { MAX_QUESTION_CHARS } from "@/utils/chatLimits";

const toast = useToast()

// ✅ Marked konfigürasyonu
marked.setOptions({
  breaks: true,  // \n => <br>
  gfm: true,     // GitHub Flavored Markdown
});

const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL;

export default {
    name: "LawDecisionAssistantPlain",
    components: {
        PrecedentSearchModal,
        DocumentPreviewModal,
        GuidePanel,
        ChatComposer,
    },
    data() {
        return {
            user: null,
            email: "",
            displayName: "",
            userInput: "",
            docType: "law",
            selectedModel: (() => {
              // Yalnız aktif seçenekler geçerli; eski/pasif tercih (sonnet/auto/deepseek/...) → GPT'ye düş.
              const allowed = ["openai"];  // Sonnet geri açılınca buraya "claude-sonnet" ekle
              const saved = localStorage.getItem("preferred_model");
              return allowed.includes(saved) ? saved : "openai";
            })(),
            modelOptions: [
              { value: "openai",        label: "GPT" },
              // Sonnet kullanıcılara kapalı. Geri açmak için: disabled satırını sil.
              { value: "claude-sonnet", label: "Sonnet", disabled: true },
            ],
            // Hero örnek soruları — gerçek kullanım kalıplarından (yasa / emsal / taraf)
            heroSamples: [
              "Fasıl 154 Madde 99 neyi düzenler?",
              "Tanık şahadetinin değerlendirilmesiyle ilgili emsal karar bul",
              "Ara emri verilmesinin koşulları nelerdir?",
            ],
            loading: false,
            messages: [
                {
                sender: "assistant",
                text: "Merhaba, size nasıl yardımcı olabilirim?",
                timestamp: new Date(),
                isWelcome: true,
                },
            ],
            suggestions: [],
            documents: [],
            totalQuestions: null,
            usedQuestions: null,

            hasSubscription: null,
            subscriptionActive: false,
            subscriptionPeriodEnd: null,

            queryStartTime: null,
            elapsedTime: 0,
            timerInterval: null,

            quotaVisible: false,
            sourcesVisible: false,

            copiedMessageIndex: null,
            showScrollButton: false,

            lastFailedQuestion: null,
            pendingQuestion: null,

            // Conversation History
            conversations: [],
            currentConversationId: null,

            // 🔥 Event handlers (removeEventListener için)
            conversationSelectHandler: null,
            newChatHandler: null,
            conversationDeletedHandler: null,

            shareHandler: null,
            clearChatHandler: null,
            exportPdfHandler: null,
            exportWordHandler: null,

            // Streaming
            useStreaming: true,
            streamingText: "",
            eventSource: null,

            // Document Preview
            previewDocument: null,

            exampleQuestions: [
                "Kira sözleşmesi nasıl feshedilir?",
                "Miras paylaşımı nasıl yapılır?",
                "İş kazası tazminatı nasıl hesaplanır?",
                "Boşanma davası nasıl açılır?",
            ],
            shouldAutoScroll: true,
            pendingClearChatItem: null,
            
            sharedLoading: false,
            sharedError: null,
            sharedMeta: null,
            unwatchShareRoute: null, 
            shareDialogVisible: false,
            shareUrl: '',

            // Emsal Karar Arama
            precedentSearchVisible: false,
            precedentSearchHandler: null,

            // Döküman Önizleme
            documentPreviewVisible: false,
            selectedDocument: null,
            downloadingSourceFile: null,
            abortController: null,
            guideVisible: false,
            principlesVisible: false,

            hoverRating: {},    // { [msgKey]: number (0-5) }
            commentOpen: {},   // { [msgKey]: boolean }
            commentText: {},   // { [msgKey]: string }
            glowedMessages: {},
            glowCounter: {},
            ratingBlockMsg: "",

        };
    },

    mounted() {

        this.loadUserFromLocalStorage(); // hızlı gösterim
        this.fetchCurrentUser();         // doğru veri

        // ESC tuşu ile streaming durdurma
        this._onKeyDown = (e) => {
            if (e.key === "Escape" && this.loading) {
                this.stopStreaming();
            }
        };
        document.addEventListener("keydown", this._onKeyDown);

        window.addEventListener("auth-changed", this.onAuthChanged);

        this.fetchQuota();
        this.fetchSubscriptionStatus();
        this.fetchConversations();

        // ✅ Sidebar: sohbet seçimi
        this.conversationSelectHandler = (event) => {
            const conv = event?.detail;
            if (!conv) return;
            this.onConversationSelected(conv);
        };
        window.addEventListener("decisions-ai-conversation-select", this.conversationSelectHandler);

        // ✅ Sidebar: yeni sohbet
        this.newChatHandler = () => {
        this.newConversation();
        };
        window.addEventListener("decisions-ai-new-chat", this.newChatHandler);

        // ✅ Sidebar: sohbet silindi (açık sohbet silinirse reset)
        this.conversationDeletedHandler = (event) => {
            const deletedId = event?.detail;
            if (!deletedId) return;

            if (this.currentConversationId === deletedId) {
                this.newConversation();
            }
        };
        window.addEventListener("decisions-ai-conversation-deleted", this.conversationDeletedHandler);

        // Retry butonu
        window.retryLastQuestion = () => {
            if (this.lastFailedQuestion) {
                this.userInput = this.lastFailedQuestion;
                this.sendMessage();
            }
        };

        window.goPricing = () => {
            this.$router.push({ path: "/admin/pricing" });
        };

        this.$nextTick(() => this.updateScrollButton());
        window.addEventListener("resize", this.updateScrollButton);

        // ✅ Header: paylaş
        this.shareHandler = () => {
            this.shareConversation();
        };
        window.addEventListener("decisions-ai-share-conversation", this.shareHandler);

        // ✅ Header: sohbeti temizle
        this.clearChatHandler = () => {
            this.confirmChatDelete();
        };
        window.addEventListener("decisions-ai-clear-chat", this.clearChatHandler);

        // ✅ Header: PDF
        this.exportPdfHandler = () => {
            this.exportToPDF();
        };
        window.addEventListener("decisions-ai-export-pdf", this.exportPdfHandler);

        // ✅ Header: Word
        this.exportWordHandler = () => {
            this.exportToWord();
        };
        window.addEventListener("decisions-ai-export-word", this.exportWordHandler);

        // ✅ Header: Emsal Karar Arama
        this.precedentSearchHandler = () => {
            this.precedentSearchVisible = true;
        };
        window.addEventListener("decisions-ai-precedent-search", this.precedentSearchHandler);

        this.checkSharedConversationFromRoute();
        this.unwatchShareRoute = this.$watch(
            () => this.$route.query.share_id,
            () => this.checkSharedConversationFromRoute()
        );

        this.checkConversationFromRoute();
        this.unwatchConversationRoute = this.$watch(
            () => this.$route.query.conversation_id,
            () => this.checkConversationFromRoute()
        );

    },

    beforeUnmount() {
        delete window.retryLastQuestion;
        delete window.goPricing;

        document.removeEventListener("keydown", this._onKeyDown);
        if (this.abortController) {
            this.abortController.abort();
        }

          // global eventler
        window.removeEventListener("auth-changed", this.onAuthChanged);
        window.removeEventListener("resize", this.updateScrollButton);

        if (this.conversationSelectHandler) {
            window.removeEventListener(
            "decisions-ai-conversation-select",
            this.conversationSelectHandler
            );
        }
        if (this.newChatHandler) {
            window.removeEventListener(
            "decisions-ai-new-chat",
            this.newChatHandler
            );
        }
        if (this.conversationDeletedHandler) {
            window.removeEventListener(
            "decisions-ai-conversation-deleted",
            this.conversationDeletedHandler
            );
        }

        // ✅ share / clear / pdf listener’larını da kaldır
        if (this.shareHandler) {
            window.removeEventListener(
            "decisions-ai-share-conversation",
            this.shareHandler
            );
        }
        if (this.clearChatHandler) {
            window.removeEventListener(
            "decisions-ai-clear-chat",
            this.clearChatHandler
            );
        }
        if (this.exportPdfHandler) {
            window.removeEventListener(
            "decisions-ai-export-pdf",
            this.exportPdfHandler
            );
        }
        if (this.exportWordHandler) {
            window.removeEventListener(
            "decisions-ai-export-word",
            this.exportWordHandler
            );
        }
        if (this.precedentSearchHandler) {
            window.removeEventListener(
            "decisions-ai-precedent-search",
            this.precedentSearchHandler
            );
        }

        // route watcher'larını kapat
        if (this.unwatchShareRoute) {
            this.unwatchShareRoute();
            this.unwatchShareRoute = null;
        }
        if (this.unwatchConversationRoute) {
            this.unwatchConversationRoute();
            this.unwatchConversationRoute = null;
        }
    },

    watch: {
        precedentSearchVisible(newVal) {
            if (!newVal) {
                // Modal kapandığında sidebar'a bildir
                window.dispatchEvent(new Event("decisions-ai-precedent-search-closed"));
            }
        },
        messages: {
            deep: true,
            handler(msgs) {
                msgs.forEach(msg => {
                    if (msg.sender === 'assistant' && !msg.isStreaming && !msg.isWelcome && !msg.rating) {
                        const key = msg.questionId ?? String(msg.timestamp);
                        if (key && !this.glowedMessages[key]) {
                            this.glowedMessages[key] = true;
                        }
                    }
                });
            }
        }
    },

    methods: {
        shouldGlow(msg) {
            if (msg.rating) return false;
            const key = msg.questionId ?? String(msg.timestamp);
            return key ? !!this.glowedMessages[key] : false;
        },

        triggerGlow(msg) {
            const key = msg.questionId ?? String(msg.timestamp);
            if (!key) return;
            this.glowedMessages[key] = true;
            this.glowCounter[key] = (this.glowCounter[key] || 0) + 1;
            this.$nextTick(() => {
                const el = document.querySelector('.rating-glow');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        },

        onAuthChanged() {
            this.loadUserFromLocalStorage();
            this.fetchCurrentUser();
        },

        escapeHtml(str) {
            if (!str) return '';
            return String(str)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        },

        // ✅ Hukuki metinleri formatla (bold yap)
        formatLegalText(html) {
            if (!html) return html;

            // Zaten <strong> içinde olan kısımları koruyarak işle
            // HTML'i strong tag'leri ve arasındaki metinler olarak parçala
            const parts = html.split(/(<strong>[\s\S]*?<\/strong>)/g);

            return parts.map(part => {
                // Eğer bu part zaten strong tag'i ise, dokunma
                if (part.startsWith('<strong>')) {
                    return part;
                }

                let result = part;

                // Hukuki etiketler: "Dava No:", "Dağıtım No:", "Mahkeme:", "Konu:", "Taraflar:", "Alıntı:", "Atıf:"
                result = result.replace(/(Dava\s+No|Dağıtım\s+No|Dağıtım|Konu|Taraflar|Alıntı|Atıf|Mahkeme)\s*:/giu, '<strong>$1:</strong>');

                // Yasa isimleri: "16/2019 sayılı ... Yasası"
                result = result.replace(/(\d+\/\d+\s+sayılı\s+[A-ZİÜÖÇŞĞIa-züöçşğıi\s]+Yasası)/gu, '<strong>$1</strong>');

                // Genel yasa isimleri: "... Yasası" (sadece strong içinde olmayanlar)
                result = result.replace(/([A-ZİÜÖÇŞĞI][a-züöçşğıi\s]+Yasası)/gu, '<strong>$1</strong>');

                // Madde numaraları: "Madde 5", "madde 106"
                result = result.replace(/((?:M|m)adde\s+\d+(?:\/[A-Z])?)/gu, '<strong>$1</strong>');

                // Emir/Nizam numaraları: "Emir 48", "Nizam 21"
                result = result.replace(/((?:E|e)mir\s+\d+[A-Z]?)/gu, '<strong>$1</strong>');
                result = result.replace(/((?:N|n)izam\s+\d+[A-Z]?)/gu, '<strong>$1</strong>');

                // Fıkra numaraları: "(1)", "(2)"
                result = result.replace(/(\(\d+\))/gu, '<strong>$1</strong>');

                // Bent harfleri: "(a)", "(b)"
                result = result.replace(/(\([a-zğüşıöçi]\))/gu, '<strong>$1</strong>');

                // Fasıl: "Fasıl 154"
                result = result.replace(/(Fasıl\s+\d+)/gu, '<strong>$1</strong>');

                // Tüzük isimleri
                result = result.replace(/([A-ZİÜÖÇŞĞI][A-ZİÜÖÇŞĞIa-züöçşğıi\s]+Tüzüğü)/gu, '<strong>$1</strong>');

                // Dava/dağıtım numaraları mono künye stiline (.case-no) sarılır.
                // Tag'lerin içine (href vb.) dokunmamak için yalnız metin
                // düğümlerinde çalışır; tarihler (23/03/2022) lookaround ile dışlanır.
                result = result.split(/(<[^>]+>)/g).map(seg => {
                    if (seg.startsWith('<')) return seg;
                    return seg
                        .replace(/\b(D\.\s?\d{1,4}\/\d{2,4})\b/gu, '<span class="case-no">$1</span>')
                        .replace(/(?<![/\d.])\b(\d{1,4}\/\d{2,4})\b(?![/\d])/gu, '<span class="case-no">$1</span>');
                }).join('');

                return result;
            }).join('');
        },

        async copyToClipboard(text) {
            try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                return true;
            }
            } catch (e) {
            console.error('Clipboard API hatası:', e);
            }

            // Fallback
            try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            textArea.style.top = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textArea);
            return ok;
            } catch (err) {
            console.error('Fallback kopyalama hatası:', err);
            return false;
            }
        },

        async copyShareUrl() {
            if (!this.shareUrl) return;
            const ok = await this.copyToClipboard(this.shareUrl);
            if (ok) {
            toast.info('Link panoya kopyalandı');
            this.shareDialogVisible = false;
            } else {
            toast.warning('Kopyalanamadı, linki elle seçip kopyalayabilirsiniz.');
            }
        },

        cancelDelete () {
            this.pendingClearChatItem = null;
        },

        updateScrollButton() {
            const el = this.$refs.messageContainer;
            if (!el) return;

            const threshold = 140;
            const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;

            this.showScrollButton = !nearBottom;
        },

        // ── Typewriter (smoothing) buffer ──────────────────────────────────
        // Backend delta'ları öbek öbek geliyor (~400ms'de bir 30-70 karakter).
        // Ham metni streamingText'te biriktiriyoruz; bu döngü ekrana SABİT, akıcı
        // hızda harf harf döküyor. _displayLen = şu an gösterilen karakter sayısı.

        // Verilen uzunluğa kadar olan kısmı parse edip ekrana basar.
        renderDisplayed(messageIndex, len) {
            const shown = this.streamingText.slice(0, len);
            let html = marked.parse(shown);
            html = this.formatLegalText(html);
            this.messages[messageIndex].text = html;
            this.$nextTick(() => this.updateScrollButton());
        },

        startTypewriter(messageIndex) {
            if (this._typewriterRaf) return;   // zaten dönüyor

            const tick = () => {
                const pending = this.streamingText.length - this._displayLen;

                if (pending > 0) {
                    // Sabit taban hız (~ortalama akışa denk) + sadece backlog
                    // büyürse devreye giren catch-up. Böylece öbekler arasında
                    // ekran boş kalmaz (sürekli akış), büyük öbekte de geri kalmaz.
                    const baseline = 2;                          // ~frame başına karakter
                    const catchup  = Math.ceil(pending / 20);    // backlog büyürse hızlan
                    const step = Math.max(baseline, catchup);
                    this._displayLen = Math.min(this.streamingText.length, this._displayLen + step);
                    this.renderDisplayed(messageIndex, this._displayLen);
                }

                // Stream sürüyorsa VEYA hâlâ gösterilecek backlog varsa devam et.
                if (this._streamActive || this._displayLen < this.streamingText.length) {
                    this._typewriterRaf = requestAnimationFrame(tick);
                } else {
                    this._typewriterRaf = null;
                }
            };

            this._typewriterRaf = requestAnimationFrame(tick);
        },

        stopTypewriter() {
            if (this._typewriterRaf) {
                cancelAnimationFrame(this._typewriterRaf);
                this._typewriterRaf = null;
            }
        },

        // complete / abort'ta: döngüyü durdur, kalan tüm metni anında göster.
        flushTypewriter(messageIndex) {
            this.stopTypewriter();
            this._displayLen = this.streamingText.length;
            this.renderDisplayed(messageIndex, this._displayLen);
        },


        onScrollToBottomClick() {
            const el = this.$refs.messageContainer;
            if (!el) return;

            this.shouldAutoScroll = true;    // ✅ kilidi aç
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });

            requestAnimationFrame(() => {
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                this.updateScrollButton();
            });
        },

        isNearBottom(threshold = 120) {
            const el = this.$refs.messageContainer;
            if (!el) return true;
            return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
            },

        autoScroll(force = false) {
            const el = this.$refs.messageContainer;
            if (!el) return;

            if (force) this.shouldAutoScroll = true; // force ise kilidi aç

            if (!this.shouldAutoScroll) return;

            // DOM büyüdüğü anı kaçırmamak için çift RAF
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                el.scrollTop = el.scrollHeight;
                });
            });
            },


        authHeader() {
            const token = localStorage.getItem("auth_token");
            return token ? { Authorization: `Bearer ${token}` } : null;
        },

        loadUserFromLocalStorage() {
            try {
                const storedUser = localStorage.getItem("auth_user");
                if (storedUser) {
                const u = JSON.parse(storedUser);
                this.user = u || null;

                this.email = u?.email || "";
                this.displayName =
                    this.getFirstName(u?.name) ||
                    this.getFirstName(u?.full_name) ||
                    (u?.email ? u.email.split("@")[0] : "") ||
                    "Kullanıcı";

                } else {
                this.user = null;
                this.email = "";
                this.displayName = "Kullanıcı";
                }
            } catch (e) {
                this.user = null;
                this.email = "";
                this.displayName = "Kullanıcı";
            }
        },

        async fetchCurrentUser() {
            const headers = this.authHeader();
            if (!headers) return;

            try {
                const res = await axios.get(`${API_BASE_URL}/user`, {
                    headers: { ...headers, "Content-Type": "application/json" },
                });

                this.user = res.data || null;

                // UI alanlarını da güncelle
                this.email = this.user?.email || this.email || "";
                this.displayName =
                    this.getFirstName(this.user?.name) ||
                    this.getFirstName(this.user?.full_name) ||
                    (this.user?.email ? this.user.email.split("@")[0] : "") ||
                    this.displayName ||
                    "Kullanıcı";


                // istersen localStorage'ı da güncel tut:
                try {
                localStorage.setItem("auth_user", JSON.stringify(this.user));
                } catch (e) { return; }


            } catch (e) {
                return;
            }
        },

        maybeShowFirstLoginGuide() {
            // Otomatik açılma devre dışı — kullanıcı sağ alttaki butonla açabilir
        },

        // GuidePanel'den "Artık Gösterme" emit'i geldiğinde çağrılır
        onGuideDismissForever() {
            try {
                const uid = this.user?.id;
                if (uid) {
                    localStorage.setItem(`mahai_guide_dismissed_${uid}`, "1");
                }
            } catch (e) { /* sessiz geç */ }
            this.guideVisible = false;
        },

        getFirstName(fullName) {
            if (!fullName) return "";
            return String(fullName).trim().split(/\s+/)[0];
        },

                // ✅ Sidebar sohbet tıklayınca buraya düşer
        async onConversationSelected(conv) {
            if (!conv?.id) return;

            // aynı sohbet ise tekrar yüklemeyelim (istersen kaldırabilirsin)
            if (this.currentConversationId === conv.id) return;
            
            await this.loadConversation(conv.id);
        },

        formatTime(date) {
            if (!date) return "";
            const d = new Date(date);
            const now = new Date();
            const diff = now - d;

            if (diff < 86400000 && d.getDate() === now.getDate()) {
                return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
            }

            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) {
                return "Dün " + d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
            }

            return (
                d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" }) +
                " " +
                d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
            );
        },

            // ✅ Yeni sohbet (tam reset)
        newConversation() {
            this.messages = [
                {
                sender: "assistant",
                text: "Merhaba, size nasıl yardımcı olabilirim?",
                timestamp: new Date(),
                isWelcome: true,
                },
            ];
            this.suggestions = [];
            this.documents = [];
            this.pendingQuestion = null;
            this.currentConversationId = null;
            this.showScrollButton = false;

            // preview kapat
            this.previewDocument = null;
            document.body.style.overflow = "";

            this.$nextTick(() => this.autoScroll(true));
            },

        async fetchConversations() {
            const token = localStorage.getItem("auth_token");
            if (!token) return;

            try {
                const res = await axios.get(`${API_BASE_URL}/conversations`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.data.ok) {
                    this.conversations = res.data.conversations || [];
                }
            } catch (err) {
                console.error("Sohbetler yüklenemedi:", err);
            }
        },

        async loadConversation(id) {
            const token = localStorage.getItem("auth_token");
            if (!token) return;

            try {
                this.loading = true;
                this.currentConversationId = id;

                const res = await axios.get(`${API_BASE_URL}/conversations/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (res.data?.ok) {
                    const apiMessages = res.data.messages || res.data.conversation?.messages || [];
                    const mapped = this.normalizeConversationMessages(apiMessages);

                    const conv = res.data.conversation;
                    if (conv && conv.id) {
                        this.currentConversationId = conv.id;
                    }

                    this.messages = mapped.length
                        ? mapped
                        : [{
                            sender: "assistant",
                            text: "Merhaba, size nasıl yardımcı olabilirim?",
                            timestamp: new Date(),
                            isWelcome: true,
                        }];

                    this.pendingQuestion = null;
                    this.suggestions = [];
                    this.documents = [];
                    this.previewDocument = null;
                    document.body.style.overflow = "";

                    this.broadcastConversationChange && this.broadcastConversationChange();

                    this.$nextTick(() => this.updateScrollButton());
                }
            } catch (err) {
                console.error("Sohbet yüklenemedi:", err);
            } finally {
                this.loading = false;
                this.$nextTick(() => {
                    setTimeout(() => {
                        this.scrollToBottomAfterRender();
                    }, 0);
                });
            }
        },




        extractSuggestionsFromHtml(html) {
            const suggestions = [];
            if (!html || typeof html !== "string") {
                return { cleanedHtml: html || "", suggestions };
            }

            const blockRegex = /<h3[^>]*>\s*Önerilen sorular\s*<\/h3>\s*<ul[^>]*>([\s\S]*?)<\/ul>/i;
            const blockMatch = html.match(blockRegex);

            if (blockMatch) {
                const ulContent = blockMatch[1] || "";
                const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;
                let m;
                while ((m = liRegex.exec(ulContent)) !== null) {
                const raw = m[1] || "";
                const text = raw
                    .replace(/<[^>]+>/g, "")
                    .replace(/&nbsp;/gi, " ")
                    .replace(/\s+/g, " ")
                    .trim();
                if (text) suggestions.push(text);
                }

                const cleanedHtml = html.replace(blockRegex, "").trim();
                return { cleanedHtml, suggestions };
            }

            return { cleanedHtml: html, suggestions };
        },

        normalizeResults(results) {
            if (!Array.isArray(results)) return [];
            const out = [];

            for (const r of results) {
                const doc = r.document || {};
                const derivedFields = (doc.derivedStructData && doc.derivedStructData.fields) || {};
                const structFields = (doc.structData && doc.structData.fields) || {};

                const getField = (fields, key) => {
                const f = fields[key];
                if (!f) return null;
                if (typeof f === "string") return f;
                if (f.stringValue) return f.stringValue;
                if (f.numberValue != null) return String(f.numberValue);
                return null;
                };

                const title =
                getField(derivedFields, "title") ||
                getField(structFields, "title") ||
                doc.id ||
                "İsimsiz belge";

                const possibleLink =
                getField(derivedFields, "link") ||
                getField(structFields, "link") ||
                getField(derivedFields, "uri") ||
                getField(structFields, "uri") ||
                doc.uri ||
                null;

                if (!possibleLink) continue;

                const url = possibleLink;

                let snippet = "";
                if (r.snippetInfo && r.snippetInfo.snippet) snippet = r.snippetInfo.snippet;

                let kind = "file";
                if (url) {
                const lower = url.toLowerCase();
                if (lower.endsWith(".pdf")) kind = "pdf";
                else if (lower.endsWith(".doc") || lower.endsWith(".docx")) kind = "word";
                }

                out.push({ title, url, snippet, kind });
            }

            return out;
        },

        quotaExceededHtml() {
        return `
            <div style="padding:12px;background:#fff7ed;border-left:3px solid #f59e0b;border-radius:10px;">
            <p style="margin:0 0 6px;font-weight:700;color:#92400e;">⚠️ Soru hakkınız doldu</p>
            <p style="margin:0;color:#92400e;">
                Devam edebilmek için lütfen Bilgi İşlem birimine başvurunuz.
            </p>
            </div>
        `;
        },


        handleQuotaExceeded() {
            // mesajı ekle
            this.messages.push({
                sender: "assistant",
                text: this.quotaExceededHtml(),
                timestamp: new Date(),
                type: "quota-exceeded",
                question: this.pendingQuestion,
            });

            // state reset
            this.pendingQuestion = null;
            this.suggestions = [];
            this.documents = [];
            this.loading = false;

            this.$nextTick(() => this.autoScroll(true));
        },


        async sendMessage() {
            const question = this.userInput.trim();
            if (!question || this.loading) return;

            if (question.length > MAX_QUESTION_CHARS) {
                this.messages.push({
                    sender: 'assistant',
                    text: `Gönderdiğiniz metin çok uzun, lütfen okunmamış mahkeme kararlarını yapay zeka sistemi ile paylaşmayınız.`,
                    timestamp: new Date(),
                });
                this.userInput = '';
                this.loading = false;
                this.$nextTick(() => this.autoScroll(true));
                return;
            }


            const token = localStorage.getItem("auth_token");
            if (!token) {
                this.messages.push({
                sender: "assistant",
                text: "Soru sorabilmek için lütfen önce giriş yapınız.",
                timestamp: new Date(),
                });
                this.userInput = "";
                this.$nextTick(() => this.autoScroll(true));
                return;
            }

            const userMessage = { sender: "user", text: question, timestamp: new Date() };
            this.messages.push(userMessage);
            this.pendingQuestion = question;

            this.userInput = "";
            this.loading = true;

            // ✅ Kullanıcı mesajı gönderdiğinde en alta scroll
            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToBottom();
                    this.updateScrollButton();
                }, 50);
            });



            try {
                await Promise.all([this.ensureSubscriptionStatus(), this.fetchQuota()]);

                if (!this.hasSubscription) {
                    if (
                        this.totalQuestions !== null &&
                        this.usedQuestions !== null &&
                        this.usedQuestions >= this.totalQuestions
                    ) {
                        this.handleQuotaExceeded();
                        this.pendingQuestion = null;
                        this.suggestions = [];
                        this.documents = [];
                        this.loading = false;
                        this.$nextTick(() => this.autoScroll(true));
                        return;
                    }
                } else {
                    if (!this.subscriptionActive) {
                        this.handleQuotaExceeded();
                        this.pendingQuestion = null;
                        this.suggestions = [];
                        this.documents = [];
                        this.loading = false;
                        this.$nextTick(() => this.autoScroll(true));
                        return;
                    }

                    if (
                        this.totalQuestions !== null &&
                        this.usedQuestions !== null &&
                        this.usedQuestions >= this.totalQuestions
                    ) {
                        this.handleQuotaExceeded();
                        this.pendingQuestion = null;
                        this.suggestions = [];
                        this.documents = [];
                        this.loading = false;
                        this.$nextTick(() => this.autoScroll(true));
                        return;
                    }
                }

                this.queryStartTime = Date.now();
                this.elapsedTime = 0;
                this.timerInterval = setInterval(() => {
                this.elapsedTime = ((Date.now() - this.queryStartTime) / 1000).toFixed(1);
                }, 100);

                const conversationHistory = this.messages
                .filter((msg) => msg.sender && msg.text)
                .map((msg) => ({
                    sender: msg.sender,
                    text: typeof msg.text === "string" ? msg.text.replace(/<[^>]+>/g, "") : msg.text,
                }));

                if (this.useStreaming) {
                    await this.sendMessageStreaming(question, token, conversationHistory);
                } else {
                    await this.sendMessageNormal(question, token, conversationHistory);
                this.$nextTick(() => this.updateScrollButton());
                }
            } catch (err) {
                
                const status = err?.response?.status;
                this.lastFailedQuestion = question;

                let errorMessage = "";
                if (status === 401) {
                errorMessage = `
                    <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                    <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">🔒 Oturum Hatası</p>
                    <p style="margin: 0; color: #7f1d1d;">Oturumunuzun süresi dolmuş olabilir. Lütfen tekrar giriş yapınız.</p>
                    </div>
                `;
                } else if (status === 500) {
                errorMessage = `
                    <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                    <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">⚠️ Sunucu Hatası</p>
                    <p style="margin: 0 0 12px; color: #7f1d1d;">Sunucuda bir hata oluştu. Lütfen daha sonra tekrar deneyin.</p>
                    <button onclick="window.retryLastQuestion()" style="padding: 6px 14px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500;">
                        🔄 Tekrar Dene
                    </button>
                    </div>
                `;
                } else {
                errorMessage = `
                    <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                    <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">❌ Bağlantı Hatası</p>
                    <p style="margin: 0 0 12px; color: #7f1d1d;">Sunucuya bağlanırken bir hata oluştu. İnternet bağlantınızı kontrol edin.</p>
                    <button onclick="window.retryLastQuestion()" style="padding: 6px 14px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500;">
                        🔄 Tekrar Dene
                    </button>
                    </div>
                `;
                }

                this.messages.push({
                sender: "assistant",
                text: errorMessage,
                timestamp: new Date(),
                type: "error",
                question: this.pendingQuestion,
                });
                this.pendingQuestion = null;
            } finally {
                this.loading = false;

                if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
                }

                
            }
            
        },

        askSuggestion(question) {
            if (!question || this.loading) return;
            this.userInput = question;
            this.sendMessage();
        },

        selectModel(value) {
            // Pasif (disabled) modeller seçilemez — ör. şu an Sonnet kullanıcılara kapalı.
            const opt = this.modelOptions.find((o) => o.value === value);
            if (opt && opt.disabled) return;
            this.selectedModel = value;
            localStorage.setItem("preferred_model", value);
        },

        // Model anahtarını (deepseek/claude-sonnet...) kullanıcıya gösterilecek etikete çevir.
        modelLabel(value) {
            const opt = this.modelOptions.find((o) => o.value === value);
            return opt ? opt.label : value;
        },

        async sendMessageNormal(question, token, conversationHistory) {
            const res = await axios.post(
                `${API_BASE_URL}/chat/ask`,
                {
                question: question,
                docType: this.docType,
                conversation_id: this.currentConversationId,
                conversation_history: conversationHistory,
                },
                {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                }
            );

            const data = res.data || {};

            const newConvId =
                data.conversation_id ||
                data.conversation?.id ||
                data.conversationId ||
                null;
            if (newConvId) this.currentConversationId = newConvId;

            if (data.error === "quota_exceeded") {
                if (data.total_limit != null && data.used_count != null) {
                this.totalQuestions = data.total_limit;
                this.usedQuestions = data.used_count;
                } else {
                await this.fetchQuota();
                }

                this.handleQuotaExceeded();
                this.pendingQuestion = null;
                this.suggestions = [];
                this.documents = [];
                return;
            }

            if (!data.ok) {
                this.lastFailedQuestion = question;
                this.messages.push({
                    
                    sender: "assistant",
                    text: `
                        <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                        <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">Hata Olustu</p>
                        <p style="margin: 0 0 12px; color: #7f1d1d;">Cevap getirilirken bir hata olustu: ${this.escapeHtml(data.error) || "Bilinmeyen hata"}</p>
                        <button onclick="window.retryLastQuestion()" style="padding: 6px 14px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500;">
                            Tekrar Dene
                        </button>
                        </div>
                    `,
                    timestamp: new Date(),
                    type: "error",
                    question: this.pendingQuestion,
                });

                this.pendingQuestion = null;
                this.suggestions = [];
                this.documents = [];
                if (data.total_limit != null && data.used_count != null) {
                    this.totalQuestions = data.total_limit;
                    this.usedQuestions = data.used_count;
                } else {
                    await this.fetchQuota();
                }
                return;
            }

            const rawHtml = data.answer || "Bu soruyla ilgili sonuc bulunamadi.";
            const { cleanedHtml, suggestions } = this.extractSuggestionsFromHtml(rawHtml);

            const messageId =
                data.assistant_message_id || // yeni API
                data.question_id ||          // eski API ile uyum için
                null;

            this.messages.push({
                id: messageId,
                sender: "assistant",
                text: cleanedHtml,
                timestamp: new Date(),
                question: this.pendingQuestion,
                questionId: messageId,
                rating: null,
            });
            this.pendingQuestion = null;

            this.suggestions = suggestions;
            this.documents = this.normalizeResults(data.results || []);

            if (data.total_limit != null && data.used_count != null) {
                this.totalQuestions = data.total_limit;
                this.usedQuestions = data.used_count;
            } else {
                await this.fetchQuota();
            }

            // ✅ sidebar refresh
            window.dispatchEvent(new Event("decisions-ai-history-refresh"));
            this.$nextTick(() => this.autoScroll(true));
        },

        stopStreaming() {
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            // Mevcut streaming mesajını sonlandır
            const streamingMsg = this.messages.find(m => m.isStreaming);
            if (streamingMsg) {
                streamingMsg.isStreaming = false;
            }
            this.loading = false;
            this.pendingQuestion = null;
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }
        },

        async sendMessageStreaming(question, token, conversationHistory) {
            this.abortController = new AbortController();

            return new Promise((resolve, reject) => {
                const streamingMessageIndex = this.messages.length;
                this.messages.push({
                sender: "assistant",
                text: "",
                timestamp: new Date(),
                question: this.pendingQuestion,
                questionId: null,
                rating: null,
                isStreaming: true,
                routedModel: null,   // router 'auto' ise hangi model cevapladı (rozet)
                });

                this.streamingText = "";
                // Typewriter buffer state'i sıfırla
                this._displayLen = 0;
                this._streamActive = true;
                this.stopTypewriter();

                fetch(`${API_BASE_URL}/chat/ask-stream`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                },
                body: JSON.stringify({
                    question: question,
                    docType: this.docType,
                    model: this.selectedModel,
                    conversation_id: this.currentConversationId,
                    conversation_history: conversationHistory,
                }),
                signal: this.abortController.signal,
                })
                .then(async (response) => {
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = "";
                    let done = false;

                    while (!done) {
                    const result = await reader.read();
                    done = result.done;
                    if (done) break;

                    buffer += decoder.decode(result.value, { stream: true });

                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        if (!line.startsWith("data: ")) continue;

                        const jsonStr = line.slice(6).trim();
                        if (jsonStr === "[DONE]") continue;

                        try {
                        const data = JSON.parse(jsonStr);

                        if (data.error) {
                            this._streamActive = false;
                            this.stopTypewriter();
                            this.messages[streamingMessageIndex].text = `
                            <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                                <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">Hata Olustu</p>
                                <p style="margin: 0; color: #7f1d1d;">${this.escapeHtml(data.error)}</p>
                            </div>
                            `;
                            this.messages[streamingMessageIndex].isStreaming = false;
                            resolve();
                            return;
                        }

                        // Router 'auto' seçildiyse hangi modelin cevapladığını rozetle göster.
                        if (data.type === "routing" && data.model) {
                            if (this.selectedModel === "auto") {
                                this.messages[streamingMessageIndex].routedModel = this.modelLabel(data.model);
                            }
                        }

                        if (data.type === "delta" && data.text) {
                            // Ham metni tampona ekle; typewriter döngüsü ekrana
                            // sabit/akıcı hızda döker (öbek öbek gelişi yumuşatır).
                            this.streamingText += data.text;
                            this.startTypewriter(streamingMessageIndex);
                        }

                        if (data.type === "complete") {
                            // ✅ Typewriter döngüsünü durdur — yoksa geç gelen frame,
                            //    aşağıda basılan temizlenmiş HTML'in üzerine yazabilir.
                            this._streamActive = false;
                            this.stopTypewriter();
                            // ✅ Streaming ile tutarlı olması için her zaman frontend'de parse et
                            let finalHtml = marked.parse(this.streamingText);
                            // ✅ Hukuki formatlama uygula
                            finalHtml = this.formatLegalText(finalHtml);
                            const { cleanedHtml, suggestions } = this.extractSuggestionsFromHtml(finalHtml);

                            const messageId =
                                data.assistant_message_id || // SSE complete'den gelen
                                data.question_id ||          // her ihtimale karşı
                                null;

                            // ✅ Formatlı HTML'i her zaman kullan
                            this.messages[streamingMessageIndex].text = cleanedHtml;
                            this.messages[streamingMessageIndex].id = messageId;
                            this.messages[streamingMessageIndex].questionId = messageId;
                            this.messages[streamingMessageIndex].isStreaming = false;

                            // ✅ Kaynaklar bilgisini mesaja ekle (backend'den gelirse)
                            if (data.sources && Array.isArray(data.sources)) {
                                this.messages[streamingMessageIndex].sources = data.sources;
                            } else if (data.results && Array.isArray(data.results)) {
                                this.messages[streamingMessageIndex].sources = data.results;
                            }

                            // ✅ Atıf doğrulama sonucu (deterministik kontrol) → rozet
                            this.messages[streamingMessageIndex].citations = data.citations || null;

                            // ✅ yeni/aktif conversation id’yi set et
                            if (data.conversation_id || data.conversation?.id) {
                            this.currentConversationId = data.conversation_id || data.conversation.id;
                            }

                            this.pendingQuestion = null;

                            this.suggestions = suggestions;

                            if (data.total_limit != null && data.used_count != null) {
                            this.totalQuestions = data.total_limit;
                            this.usedQuestions = data.used_count;
                            }

                            // ✅ sidebar refresh
                            window.dispatchEvent(new Event("decisions-ai-history-refresh"));
                            // Scroll button durumunu güncelle (otomatik scroll kapalı)
                            this.$nextTick(() => {
                                this.updateScrollButton();
                            });

                            resolve();
                            return;
                        }
                        } catch (parseErr) {
                        console.error("JSON parse error:", parseErr, jsonStr);
                        }
                    }
                    }

                    // Stream "complete" gelmeden bitti (ör. bağlantı kapandı):
                    // typewriter'ı durdur, kalan tamponu anında göster.
                    if (this.messages[streamingMessageIndex].isStreaming) {
                    this._streamActive = false;
                    this.flushTypewriter(streamingMessageIndex);
                    this.messages[streamingMessageIndex].isStreaming = false;
                    this.pendingQuestion = null;
                    }

                    resolve();
                })
                .catch((err) => {
                    if (err.name === "AbortError") {
                        // Kullanici tarafindan durduruldu — biriken kısmi metni anında göster.
                        this._streamActive = false;
                        this.flushTypewriter(streamingMessageIndex);
                        this.messages[streamingMessageIndex].isStreaming = false;
                        resolve();
                        return;
                    }

                    // Hata mesajının üzerine geç gelen render binmesin.
                    this._streamActive = false;
                    this.stopTypewriter();
                    this.messages[streamingMessageIndex].text = `
                    <div style="padding: 12px; background: #fef2f2; border-left: 3px solid #ef4444; border-radius: 8px;">
                        <p style="margin: 0 0 8px; font-weight: 600; color: #991b1b;">Baglanti Hatasi</p>
                        <p style="margin: 0 0 12px; color: #7f1d1d;">Sunucuya baglanirken bir hata olustu.</p>
                        <button onclick="window.retryLastQuestion()" style="padding: 6px 14px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500;">
                        Tekrar Dene
                        </button>
                    </div>
                    `;
                    this.messages[streamingMessageIndex].isStreaming = false;
                    this.lastFailedQuestion = question;
                    this.pendingQuestion = null;

                    reject(err);
                });
            });
        },

        async copyMessage(htmlText) {
            try {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = htmlText;
                const plainText = tempDiv.textContent || tempDiv.innerText || "";
                await navigator.clipboard.writeText(plainText);

                const messageIndex = this.messages.findIndex((m) => m.text === htmlText);
                this.copiedMessageIndex = messageIndex;

                setTimeout(() => {
                this.copiedMessageIndex = null;
                }, 2000);
            } catch (err) {
                console.error("Kopyalama başarısız:", err);
            }
        },

        async sendRating(msg, rating) {
          const qId = msg.id ?? msg.questionId;
          if (!qId) return;
          const token = localStorage.getItem('auth_token');
          try {
            await axios.post(
              `${API_BASE_URL}/chat/feedback`,
              { question_id: qId, rating },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const messageIdx = this.messages.findIndex(m => (m.id ?? m.questionId) === qId);
            if (messageIdx !== -1) this.messages[messageIdx].rating = rating;
            // Yorum kutusu aç
            this.$set ? this.$set(this.commentOpen, qId, true) : (this.commentOpen[qId] = true);
            if (!this.commentText[qId]) this.commentText[qId] = '';
          } catch (err) {
            console.error('Rating gönderilemedi:', err);
          }
        },

        openComment(msg) {
          const qId = msg.id ?? msg.questionId;
          this.commentText[qId] = msg.comment ?? '';
          this.commentOpen[qId] = true;
        },

        async sendComment(msg) {
          const qId = msg.id ?? msg.questionId;
          if (!qId) return;
          const comment = (this.commentText[qId] ?? '').trim();
          const token = localStorage.getItem('auth_token');
          try {
            await axios.post(
              `${API_BASE_URL}/chat/feedback`,
              { question_id: qId, rating: msg.rating, comment },
              { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            const messageIdx = this.messages.findIndex(m => (m.id ?? m.questionId) === qId);
            if (messageIdx !== -1) this.messages[messageIdx].comment = comment || null;
            this.commentOpen[qId] = false;
          } catch (err) {
            console.error('Yorum gönderilemedi:', err);
          }
        },


        confirmChatDelete(){
            this.pendingClearChatItem = true;
        },

        async clearChat() {
            this.pendingClearChatItem = null;

            const token = localStorage.getItem("auth_token");

            // giriş yoksa sadece local reset
            if (!token || !this.currentConversationId) {
                this.newConversation();
                return;
            }

            try {
                await axios.delete(
                `${API_BASE_URL}/conversations/${this.currentConversationId}/messages`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
                );

                // ✅ UI reset (aynı sohbet ID kalır, içi boşalır)
                this.messages = [
                {
                    sender: "assistant",
                    text: "Merhaba, size nasıl yardımcı olabilirim?",
                    timestamp: new Date(),
                    isWelcome: true,
                },
                ];

                this.suggestions = [];
                this.documents = [];
                this.pendingQuestion = null;
                this.previewDocument = null;
                document.body.style.overflow = "";

                this.$nextTick(this.scrollToBottomAfterRender);

                toast.info('Sohbet temizlendi');

                // sidebar refresh (preview vs güncellensin)
                window.dispatchEvent(new Event("decisions-ai-history-refresh"));
            } catch {
                toast.error('Sohbet temizlenemedi');
            }
        },

        async shareConversation() {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                toast.warning('Paylaşmak için giriş yapmalısınız.');
                return;
            }

            if (this.messages.length <= 1) {
                toast.warning('Paylaşılacak mesaj yok.');
                return;
            }

            try {
                const res = await axios.post(
                `${API_BASE_URL}/conversations/share`,
                { conversation_id: this.currentConversationId, messages: this.messages },
                { headers: { Authorization: `Bearer ${token}` } }
                );

                if (!res.data.ok) {
                toast.error('Paylaşım yapılamadı');
                return;
                }

                const shareId = res.data.share_id;
                const url = `${window.location.origin}/apps/decisions-ai?share_id=${shareId}`;
                this.shareUrl = url;

                const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent);

                // 1) Mobilse: öncelik native share + modal
                if (isMobile && navigator.share) {
                try {
                    await navigator.share({
                    title: 'Mahai Sohbet Paylaşımı',
                    text: 'Mahai üzerinde paylaşılan bir sohbet.',
                    url,
                    });
                } catch (e) {
                    console.warn('navigator.share iptal / hata:', e);
                }

                // Her durumda linki gösteren modalı aç
                //this.shareDialogVisible = true;
                return;
                }

                // 2) Desktop veya navigator.share yoksa: kopyalamayı dene
                const copied = await this.copyToClipboard(url);
                if (copied) {
                toast.info('Paylaşım linki panoya kopyalandı.');
                } else {
                // Olmadıysa modal aç, kullanıcı elle kopyalasın
                //this.shareDialogVisible = true;
                }
            } catch (e) {
                console.error('Share error:', e);
                toast.error('Paylaşım yapılamadı');
            }
        },



        async exportToPDF() {
            try {
                const html2pdf = (await import("html2pdf.js")).default;
                const element = this.$refs.messageContainer;

                html2pdf()
                .set({
                    margin: 10,
                    filename: `mahai-sohbet-${Date.now()}.pdf`,
                    html2canvas: { scale: 2 },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                })
                .from(element)
                .save();
            } catch {
                toast.error('PDF oluşturulurken hata oluştu');
            }
        },

        async exportToWord() {
            try {
                // Mesajları HTML olarak hazırla
                let htmlContent = `
                    <html>
                    <head>
                        <meta charset="utf-8">
                        <style>
                            body { font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6; }
                            .message { margin-bottom: 20px; padding: 10px; border-radius: 8px; }
                            .user-message { background-color: #e8f4f8; border-left: 4px solid #5a6270; }
                            .assistant-message { background-color: #f5f5f5; border-left: 4px solid #666; }
                            .message-label { font-weight: bold; margin-bottom: 5px; color: #333; }
                            .message-time { font-size: 10pt; color: #888; margin-top: 5px; }
                            h1 { color: #5a6270; border-bottom: 2px solid #5a6270; padding-bottom: 10px; }
                        </style>
                    </head>
                    <body>
                        <h1>Mahai Sohbet Kaydı</h1>
                        <p style="color: #666; font-size: 10pt;">Oluşturulma: ${new Date().toLocaleString('tr-TR')}</p>
                        <hr style="border: 1px solid #ddd; margin: 20px 0;">
                `;

                for (const msg of this.messages) {
                    const isUser = msg.sender === 'user';
                    const label = isUser ? 'Siz' : 'Mahai';
                    const className = isUser ? 'user-message' : 'assistant-message';
                    const time = this.formatTime(msg.timestamp);

                    // HTML içeriğini temizle (script tag'leri vs.)
                    const cleanText = msg.text
                        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

                    htmlContent += `
                        <div class="message ${className}">
                            <div class="message-label">${label}</div>
                            <div class="message-content">${cleanText}</div>
                            <div class="message-time">${time}</div>
                        </div>
                    `;
                }

                htmlContent += `
                    </body>
                    </html>
                `;

                // Word dosyası olarak indir (MIME type ile)
                const blob = new Blob(['\ufeff', htmlContent], {
                    type: 'application/msword'
                });

                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `mahai-sohbet-${Date.now()}.doc`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

                toast.success('Word dosyası indirildi');
            } catch (err) {
                console.error('Word export error:', err);
                toast.error('Word dosyası oluşturulurken hata oluştu');
            }
        },

        addPrecedentToChat(text) {
            // Emsal karar metnini input'a ekle
            this.userInput = text;
            this.precedentSearchVisible = false;
            toast.success('Emsal karar sohbete eklendi');
        },

        // ===== DÖKÜMAN ÖNİZLEME VE İNDİRME =====

        openDocumentPreview(doc) {
            this.selectedDocument = doc;
            this.documentPreviewVisible = true;
        },

        handleDocumentAddToChat(text) {
            this.userInput = text;
            this.documentPreviewVisible = false;
            toast.success('Kaynak sohbete eklendi');
        },

        async downloadSourceFile(doc) {
            if (!doc?.file_name) {
                toast.error('Dosya adı bulunamadı');
                return;
            }

            this.downloadingSourceFile = doc.file_name;

            try {
                const token = localStorage.getItem("auth_token");
                const fileId = doc.file_id || "local";

                const response = await axios.get(
                    `${API_BASE_URL}/files/${fileId}/download`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { filename: doc.file_name },
                        responseType: "blob",
                    }
                );

                // Blob'dan dosya indir
                const blob = new Blob([response.data], {
                    type: response.headers["content-type"] || "application/octet-stream",
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = doc.file_name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                toast.success('Dosya indiriliyor...');
            } catch (err) {
                console.error("[LawAssistant] Download error:", err);

                if (err?.response?.status === 404) {
                    toast.warning("Dosya bulunamadı. Lütfen mevzuat.mahkemeler.net adresinden kontrol ediniz.", {
                        timeout: 6000,
                    });
                } else {
                    toast.error("Dosya indirilirken bir hata oluştu.");
                }
            } finally {
                this.downloadingSourceFile = null;
            }
        },

        groupedSources(sources) {
            const out = { yasatuzuk: [], karar: [], kitap: [], unknown: [] };
            if (!Array.isArray(sources)) return out;
            for (const s of sources) {
                const folder = String(s?.folder || '').toLowerCase();
                if (folder === 'yasatuzuk') out.yasatuzuk.push(s);
                else if (folder === 'karar' || folder === 'kararlar') out.karar.push(s);
                else if (folder === 'kitap' || folder === 'kitaplar') out.kitap.push(s);
                else out.unknown.push(s);
            }
            return out;
        },

        // Kaynakları tek v-for için gruplanmış diziye çevir (boş gruplar elenir).
        sourceGroups(sources) {
            const g = this.groupedSources(sources);
            return [
                { key: 'y', title: 'Yasa & Tüzükler', icon: 'gavel', items: g.yasatuzuk },
                { key: 'k', title: 'Emsal Kararlar', icon: 'description', items: g.karar },
                // Doktrin bağlayıcı kaynaklardan SONRA gelir — sıralama kasıtlı.
                { key: 'd', title: 'Doktrin / Kitaplar', icon: 'menu_book', items: g.kitap },
                { key: 'u', title: 'Diğer Kaynaklar', icon: 'article', items: g.unknown },
            ].filter((grp) => grp.items && grp.items.length);
        },

        // Künye kartı: citation "Yargitay Hukuk | Dava No: 159/2013 | Dağıtım: D.33/2016"
        // → mahkeme eyebrow'u ve mono numara satırına ayrıştırılır.
        kunyeCourt(src) {
            // Kitaplarda eyebrow satırı eser adıdır; citation'ın ilk parçası
            // "Yazar, Eser, Basım" olduğu için ondan değil metadata'dan okunur.
            if (src && src.eser_adi) return src.eser_adi;
            const c = (src && src.citation) || '';
            if (!c.includes('|')) return null;
            return c.split('|')[0].trim();
        },

        kunyeNumbers(src) {
            const c = (src && src.citation) || '';
            if (c.includes('|')) {
                return c.split('|').slice(1)
                    .map(s => s.trim().replace(/^Dağıtım:\s*/i, '').replace(/^Dava No:\s*/i, 'Dava '))
                    .filter(Boolean)
                    .join(' · ');
            }
            // Yasa kaynakları: citation numaralı bir madde referansıysa göster
            return /\d/.test(c) && c !== (src && src.file_name) ? c : null;
        },

        // Doğrulama mührü: kartın numaraları backend atıf kontrolünden
        // (msg.citations) doğrulandıysa altın mühür; doğrulanamadıysa uyarı.
        sealState(msg, src) {
            const cit = msg && msg.citations;
            if (!cit || !src || !src.citation) return null;
            const nums = src.citation.match(/\d{1,4}\/\d{2,4}/g) || [];
            if (!nums.length) return null;
            const ok = new Set([...(cit.verified_mcp || []), ...(cit.verified_corpus || [])]);
            const bad = new Set(cit.unverified || []);
            if (nums.some(n => ok.has(n))) return 'ok';
            if (nums.some(n => bad.has(n))) return 'warn';
            return null;
        },

        // Kaynak kartının ana satırı. Kitaplarda dosya adı bir slug'dır
        // ("chitty-on-contracts-32") — okunur künye metadata'dan kurulur.
        sourceDisplayName(src) {
            if (!src) return '';
            if (src.eser_adi) {
                return [src.yazar, src.basim].filter(Boolean).join(' · ') || src.eser_adi;
            }
            return src.yasa_adi || this.formatSourceDisplayName(src.file_name);
        },

        formatSourceDisplayName(filename) {
            if (!filename) return filename;
            const name = filename.replace(/\.(docx?|pdf)$/i, '');

            // ANAYASA
            if (/^anayasa$/i.test(name)) return 'KKTC Anayasası';

            // Fasıl: yasa-ve-tuzuk_f_{N} veya f_{N}
            let m = name.match(/^(?:yasa[-_]ve[-_]tuzuk[-_])?f[_-](\d+)$/i);
            if (m) return `Fasıl ${parseInt(m[1])}`;

            // Yasa numaralı: yasa-ve-tuzuk_{N}-{YYYY} veya {N}-{YYYY}
            m = name.match(/^(?:yasa[-_]ve[-_]tuzuk[-_])?0*(\d+)[_-](\d{4})$/i);
            if (m) return `${parseInt(m[1])}/${m[2]} Sayılı`;

            // Tüzük: d{N}_{YYYY}
            m = name.match(/^d(\d+)[_-](\d{4})$/i);
            if (m) return `Tüzük ${parseInt(m[1])}/${m[2]}`;

            // Karar: {courtKey}_{dno|kno|d.no|k.no}_{year}_{casePart}
            m = name.match(/^(.+?)_(dno|kno|d\.no|k\.no)_(\d{4})_(.+)$/i);
            if (m) {
                const [, courtKey, noType, year, casePart] = m;
                const court = this._courtKeyToName(courtKey);
                const noLabel = /kno|k\.no/i.test(noType) ? 'K.No' : 'D.No';
                const num = this._extractCaseNum(casePart);
                return num !== null ? `${court} ${noLabel} ${num}/${year}` : `${court} ${noLabel} ${year}`;
            }

            // Named files (Kamu_Gorevlileri_... türü)
            return name.replace(/_/g, ' ');
        },

        _courtKeyToName(key) {
            const norm = key.toLowerCase()
                .replace(/̇/g, '')   // combining dot above (İ → I → i path)
                .replace(/i̇/g, 'i');

            const map = {
                'yargitay_hukuk': 'Yargıtay Hukuk',
                'yargitay_ceza': 'Yargıtay Ceza',
                'yargitay_aile': 'Yargıtay Aile',
                'yargitay_cezaistida': 'Yargıtay Ceza İstida',
                'yargitay_hukukistida': 'Yargıtay Hukuk İstida',
                'yargitay_y.genel.istida': 'Yargıtay Y.G. İstida',
                'yargitay_asli-yetki-istida-istinaf': 'Yargıtay A.Y. İ/İ',
                'yargitay_asli-yetki-istida': 'Yargıtay A.Y. İstida',
                'yargitay_asli-yetki-istinaf': 'Yargıtay A.Y. İstinaf',
                'yargitay_asli-yetki': 'Yargıtay A.Y.',
                'yim': 'YİM',
                'yimist': 'YİM İstida',
                'anayasa': 'Anayasa Mah.',
                'disiplin': 'Disiplin Kurulu',
                'yüksek-mahkeme-disiplin': 'Y.M. Disiplin',
                '_ceza': 'Ceza',
            };
            if (map[norm]) return map[norm];
            if (norm.includes('yrg_asli')) return 'Yargıtay A.Y.';
            if (norm.includes('y.genel')) return 'Yargıtay Y.G.';
            return key.replace(/_/g, ' ').replace(/-/g, ' ');
        },

        _extractCaseNum(casePart) {
            const s = casePart.replace(/\.(docx?|pdf)$/i, '').trim();
            // D. N-YYYY veya D-N-YYYY veya D.No.N-YYYY
            let m = s.match(/D\.?\s*(?:No\.?)?\s*[-\s]?\s*(\d+)\s*[-–]\s*\d{2,4}/i);
            if (m) return parseInt(m[1]);
            // N-YYYY veya N_YYYY
            m = s.match(/^(\d+)\s*[-_]\s*\d{2,4}$/);
            if (m) return parseInt(m[1]);
            // Başta sayı
            m = s.match(/^(\d+)/);
            if (m) return parseInt(m[1]);
            return null;
        },

        truncateSourceQuote(text, maxLength = 80) {
            if (!text) return "";
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + "...";
        },

        handleScroll() {
            const el = this.$refs.messageContainer;
            if (!el) return;

            const threshold = 140;
            const nearBottom = (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;

            // ✅ kullanıcı yukarı çıktıysa kilitle
            this.shouldAutoScroll = nearBottom;

            // ✅ buton görünürlüğü
            this.showScrollButton = !nearBottom;
        },





        scrollToBottom(smooth = true) {
            // bottomAnchor'a scroll et - daha güvenilir
            const anchor = this.$refs.bottomAnchor;
            if (anchor?.scrollIntoView) {
                anchor.scrollIntoView({
                    block: 'end',
                    behavior: smooth ? 'smooth' : 'instant'
                });
                return;
            }
            // Fallback: container'a scroll
            const el = this.$refs.messageContainer;
            if (el) {
                el.scrollTo({
                    top: el.scrollHeight,
                    behavior: smooth ? 'smooth' : 'instant'
                });
            }
        },

            // ✅ yükleme sonrası en sağlam scroll
        scrollToBottomAfterRender() {
            this.$nextTick(() => {
                setTimeout(() => {
                const a = this.$refs.bottomAnchor;
                if (a?.scrollIntoView) a.scrollIntoView({ block: "end", behavior: "auto" });
                }, 0);
            });
        },


        async fetchQuota() {
            try {
                const token = localStorage.getItem("auth_token");
                if (!token) {
                this.totalQuestions = null;
                this.usedQuestions = null;
                return;
                }

                const res = await axios.get(`${API_BASE_URL}/quota`, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                });

                const data = res.data || {};
                if (data.total_limit == null || data.used_count == null) return;

                this.totalQuestions = data.total_limit;
                this.usedQuestions = data.used_count;
            } catch (e) {
                console.error("fetchQuota error", e);
            }
        },

        async fetchSubscriptionStatus() {
            try {
                const token = localStorage.getItem("auth_token");
                if (!token) {
                this.hasSubscription = null;
                this.subscriptionActive = false;
                this.subscriptionPeriodEnd = null;
                return;
                }

                // Stripe kaldırıldı; abonelik bilgisini profile üzerinden al.
                const res = await axios.get(`${API_BASE_URL}/profile`, {
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                });

                const data = res.data || {};
                const subscription = data.subscription || null;
                this.hasSubscription = !!subscription;
                this.subscriptionActive = !!subscription?.is_active;
                this.subscriptionPeriodEnd = subscription?.current_period_end || null;
            } catch (e) {
                console.error("fetchSubscriptionStatus error", e);
                if (this.hasSubscription === null) this.hasSubscription = false;
            }
        },

        async ensureSubscriptionStatus() {
            if (this.hasSubscription === null) await this.fetchSubscriptionStatus();
        },

        normalizeConversationMessages(apiMessages) {
            const list = Array.isArray(apiMessages) ? apiMessages : [];

            return list.map((m) => {
                const role = m.role || m.sender || "assistant";
                const sender = role === "user" ? "user" : "assistant";

                // attachments (DB'de kaydedilen kaynak dosyalar) → sources
                let sources = null;
                if (Array.isArray(m.attachments) && m.attachments.length) {
                    sources = m.attachments;
                } else if (Array.isArray(m.sources) && m.sources.length) {
                    sources = m.sources;
                }

                return {
                id: m.id,
                sender,
                text: m.content ?? m.text ?? "",
                timestamp: m.created_at ? new Date(m.created_at) : new Date(),
                // UI’nin kullandığı ekstra alanlar (opsiyonel)
                questionId: m.question_id ?? null,
                rating: m.rating ?? null,
                comment: m.comment ?? null,
                sources,
                isStreaming: false,
                };
            });
        },

    async checkConversationFromRoute() {
        const conversationId = this.$route.query.conversation_id;
        if (!conversationId) return;

        await this.loadConversation(Number(conversationId));

        // Query param'ı URL'den temizle
        this.$router.replace({ query: { ...this.$route.query, conversation_id: undefined } });
    },

    async checkSharedConversationFromRoute() {
        const shareId = this.$route.query.share_id;

        // URL'de share_id yoksa normal modda devam et
        if (!shareId) {
            this.sharedMeta = null;
            this.sharedError = null;
            this.sharedLoading = false;
            return;
        }

        this.sharedLoading = true;
        this.sharedError = null;

        try {
            const headers = this.authHeader() || {};

            const res = await axios.get(
                `${API_BASE_URL}/conversations/shared/${shareId}`,
                { headers }
            );

            if (!res.data.ok) {
                this.sharedError = res.data.error || "Paylaşılan sohbet bulunamadı";
                this.sharedLoading = false;
                return;
            }

            const data = res.data.data;

            // ✅ Backend’ten gelen mesajları normalize edip
            // komponentin geri kalanında kullandığın formata çeviriyoruz
            const mapped = this.normalizeConversationMessages(data.messages || []);
            this.messages = mapped.length
            ? mapped
            : [
                {
                    sender: "assistant",
                    text: "Merhaba, size nasıl yardımcı olabilirim?",
                    timestamp: new Date(),
                    isWelcome: true,
                },
                ];

            // ✅ Shared meta sadece banner için
            this.sharedMeta = {
                title: data.title,
                conversationId: data.conversation_id,
                shareId: data.share_id,
            };

            // chatStarted computed olduğu için ayrıca set etmene gerek yok

            // ✅ Mesajları aldıktan sonra en alta kaydır
            this.$nextTick(() => {
            this.scrollToBottomAfterRender();
            this.updateScrollButton();
            });
        } catch (e) {
            console.error(e);
            this.sharedError = "Paylaşılan sohbet yüklenemedi";
        } finally {
            this.sharedLoading = false;
        }
    }

},

computed: {
    loadingStatusText() {
      const t = parseFloat(this.elapsedTime) || 0;
      if (t < 3)  return "Düşünüyorum...";
      if (t < 8)  return "Kaynaklar aranıyor...";
      if (t < 20) return "Yanıt hazırlanıyor...";
      return "Yanıt biraz uzun sürebilir...";
    },

    chatStarted() {
        // ✅ ilk soruyu user sorunca true olur
        return (this.messages?.length || 0) > 1;
    },

    unratedLastMessage() {
        const assistantMsgs = this.messages.filter(
            m => m.sender === 'assistant' && !m.isWelcome && !m.isStreaming
        );
        const last = assistantMsgs[assistantMsgs.length - 1];
        return last && !last.rating ? last : null;
    },

    quotaPercent() {
      if (!this.totalQuestions) return 0;
      const pct = (this.usedQuestions / this.totalQuestions) * 100;
      return Math.max(0, Math.min(100, Math.round(pct)));
    },
    remainingQuestions() {
      if (this.totalQuestions == null || this.usedQuestions == null) return null;
      return Math.max(this.totalQuestions - this.usedQuestions, 0);
    },
    quotaColor() {
      if (this.quotaPercent >= 90) return "#EF4444";
      if (this.quotaPercent >= 70) return "#F59E0B";
      return "#3B82F6";
    },
    groupedMessages() {
      const groups = [];
      let currentGroup = null;

      this.messages.forEach((msg, index) => {
        const timestamp = msg.timestamp || new Date();
        const timeStr = this.formatTime(timestamp);
        const showTimestamp =
          index === 0 ||
          (this.messages[index - 1] &&
            Math.abs(new Date(timestamp) - new Date(this.messages[index - 1].timestamp)) >
              300000);

        if (showTimestamp || !currentGroup) {
          currentGroup = { time: timeStr, messages: [] };
          groups.push(currentGroup);
        }

        currentGroup.messages.push({ ...msg, index });
      });

      return groups;
    },
  },

};

</script>

<style scoped>
/* ===========================
   MAHAI CHAT – LEGAL SAGE THEME
   =========================== */

/* Palet: merkezi token köprüsü — tek kaynak src/assets/scss/_tokens.scss.
   Eski gri-mavi yapısal aksan kaldırıldı; tüm krom kürsü yeşiline bağlandı. */
.chat-page {
  --sage-green: var(--sage);
  --sage-green-dark: var(--sage-deep);

  --sage-primary: var(--sage);
  --sage-primary-light: #2e7259;
  --sage-primary-dark: var(--sage-deep);
  --user-bubble: var(--sage);
  --user-bubble-dark: var(--sage-deep);
  --sage-accent: var(--gold);

  --sage-bg-main: var(--paper);
  --sage-bg-secondary: #eef0ea;
  --sage-bg-assistant: var(--paper-raised);
  --sage-text-primary: var(--ink);
  --sage-text-secondary: var(--ink-secondary);
  --sage-text-muted: var(--ink-faint);
  --sage-border: var(--rule);
  --sage-border-focus: var(--sage);

  font-family: var(--font-sans);

  /* z-index ölçeği (tek kaynak; mevcut sıra korunur) */
  --z-input: 9999;
  --z-floating: 10000;
  --z-modal: 10001;
}

.chat-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--sage-bg-main);
}

/* Messages area */
.chat-body {
  flex: 1;
  overflow: auto;
  padding: 20px 16px 140px;
  scrollbar-width: thin;
  scrollbar-color: rgba(90, 98, 112, 0.3) transparent;
}
.chat-body::-webkit-scrollbar { width: 6px; }
.chat-body::-webkit-scrollbar-track { background: transparent; }
.chat-body::-webkit-scrollbar-thumb { background: rgba(90, 98, 112, 0.3); border-radius: 3px; }
.chat-body::-webkit-scrollbar-thumb:hover { background: rgba(90, 98, 112, 0.5); }

.chat-inner { max-width: 860px; margin: 0 auto; }

.chat-body.is-empty {
  padding-bottom: 16px;
}

/* HERO */
.chat-hero {
  min-height: calc(100vh - 220px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px 16px;
  text-align: center;
  background: var(--sage-bg-main);
}

.hero-eyebrow {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--sage);
  margin-bottom: 14px;
}

.chat-title {
  font-size: 34px;
  font-weight: 600;
  color: var(--ink);
  margin: 0 0 10px;
  letter-spacing: -0.3px;
  font-family: var(--font-serif);
  line-height: 1.25;
  max-width: 680px;
}

.hero-hint {
  font-size: 14.5px;
  color: var(--ink-secondary);
  max-width: 560px;
  line-height: 1.6;
}

.hero-input {
  width: 100%;
  max-width: 860px;
  margin-top: 28px;
}

.hero-samples {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  max-width: 720px;
}

.hero-sample-chip {
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--sage-deep);
  background: var(--paper-raised);
  border: 1px solid var(--rule);
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
}
.hero-sample-chip:hover {
  border-color: var(--sage);
  background: var(--sage-soft);
}
.hero-sample-chip:focus-visible {
  outline: 2px solid var(--sage);
  outline-offset: 2px;
}

/* Messages */
.messages {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 8px;
}

.msg-row { display: flex; align-items: flex-start; }
.msg-row.is-user { justify-content: flex-end; }
.msg-row.is-assistant { justify-content: flex-start; }

.msg-bubble {
  max-width: min(720px, 88%);
  background: var(--sage-bg-assistant);
  border: 1px solid var(--sage-border);
  border-left: 3px solid var(--sage-primary);
  border-radius: 4px 16px 16px 16px;
  padding: 16px 18px;
  position: relative;
  transition: box-shadow 0.15s ease;
}

.msg-bubble:hover {
  box-shadow: 0 2px 8px rgba(90, 98, 112, 0.08);
}

.msg-row.is-user .msg-bubble {
  background: linear-gradient(145deg, var(--user-bubble), var(--user-bubble-dark));
  color: #fff;
  border: 1px solid transparent;
  border-left: none;
  border-right: 3px solid var(--sage-accent);
  border-radius: 16px 4px 16px 16px;
  box-shadow: 0 2px 12px rgba(90, 98, 112, 0.25);
}

/* AI cevabı: kart/balon değil — okuma kolonunun tamamını kullanır (yükleme hariç). */
.msg-row.is-assistant { width: 100%; }
.msg-row.is-assistant .msg-bubble:not(.loading-logo-bubble) {
  max-width: 100%;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 2px 2px 4px;
}
.msg-row.is-assistant .msg-bubble:not(.loading-logo-bubble):hover {
  box-shadow: none;
}

.routed-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(26, 95, 74, 0.08);
  color: var(--sage-green);
  font-size: 11px;
  font-weight: 600;
}
.routed-badge .material-symbols-outlined {
  font-size: 14px;
}

.msg-text {
  font-size: 15px;
  line-height: 1.65;
  word-break: break-word;
}

/* AI cevabı gerekçeli karar gibi okunur: serif gövde, geniş satır aralığı.
   Kullanıcı balonu sans kalır — soru ile hüküm ayrışır. */
.msg-row.is-assistant .msg-text {
  font-family: var(--font-serif);
  font-size: 16px;
  line-height: 1.78;
  color: var(--ink);
}

/* Dava/dağıtım/madde numaraları belgenin kimliğidir — mono dizilir */
.msg-text :deep(.case-no) {
  font-family: var(--font-mono);
  font-size: 0.88em;
  font-weight: 500;
  color: var(--sage-deep);
  background: var(--sage-soft);
  padding: 0 5px;
  border-radius: 4px;
  white-space: nowrap;
}
.msg-text :deep(p) { margin: 0 0 10px; }
.msg-text :deep(p:last-child) { margin-bottom: 0; }
.msg-text :deep(ul), .msg-text :deep(ol) { padding-left: 20px; margin: 10px 0; }
.msg-text :deep(li) { margin: 5px 0; }
.msg-text :deep(code) {
  background: rgba(90, 98, 112, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: var(--sage-text-primary);
}
.msg-row.is-user .msg-text :deep(code) {
  background: rgba(255,255,255,0.2);
  color: #fff;
}
.msg-text :deep(pre) {
  background: #1c2520;
  color: #e2e8f0;
  padding: 14px 16px;
  border-radius: 10px;
  overflow-x: auto;
  margin: 10px 0;
  font-size: 13px;
}
.msg-text :deep(a) {
  color: var(--sage-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.msg-row.is-user .msg-text :deep(a) {
  color: #a8d5c2;
}

/* Bölüm başlıkları — belge hiyerarşisi: small-caps sans eyebrow + hairline.
   (Özet / Emsal Karar / Değerlendirme başlıkları resmi belge dili kazanır.) */
.msg-text :deep(h1),
.msg-text :deep(h2) {
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sage);
  margin: 26px 0 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--rule);
  line-height: 1.3;
}
.msg-text :deep(h3) {
  font-family: var(--font-serif);
  font-size: 16.5px;
  font-weight: 600;
  color: var(--ink);
  margin: 18px 0 6px;
  line-height: 1.35;
}
.msg-text :deep(h4) {
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--sage-deep);
  margin: 14px 0 5px;
  line-height: 1.3;
}
.msg-text :deep(h1:first-child),
.msg-text :deep(h2:first-child),
.msg-text :deep(h3:first-child),
.msg-text :deep(h4:first-child) {
  margin-top: 4px;
}
.msg-text :deep(blockquote) {
  margin: 10px 0;
  padding: 10px 14px;
  border-left: 3px solid var(--gold);
  background: rgba(201, 162, 39, 0.06);
  border-radius: 0 6px 6px 0;
  color: #374151;
  font-style: italic;
}
.msg-text :deep(blockquote p) { margin: 0; }

/* Tablolar (hukuki cevaplarda karşılaştırma/madde listeleri) */
.msg-text :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  font-size: 13.5px;
}
.msg-text :deep(th),
.msg-text :deep(td) {
  border: 1px solid var(--sage-border);
  padding: 7px 10px;
  text-align: left;
  vertical-align: top;
}
.msg-text :deep(th) {
  background: rgba(26, 95, 74, 0.06);
  color: var(--sage-green-dark);
  font-weight: 600;
}
.msg-text :deep(tr:nth-child(even) td) { background: rgba(0, 0, 0, 0.015); }
.msg-text :deep(hr) {
  border: none;
  border-top: 1px solid var(--sage-border);
  margin: 16px 0;
}

@keyframes reveal-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.msg-sources {
  animation: reveal-up 0.35s ease both;
}
@media (prefers-reduced-motion: reduce) {
  .msg-sources, .msg-rating-area { animation: none !important; }
}
.msg-rating-area {
  animation: reveal-up 0.35s ease 0.1s both;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(0,0,0,0.06);
}

.rating-prompt-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  animation: fadeInLabel 0.4s ease;
}

@keyframes fadeInLabel {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.rating-block-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 8px 14px;
  background: rgba(201, 162, 39, 0.12);
  border: 1px solid rgba(201, 162, 39, 0.4);
  border-radius: 8px;
  color: #92740a;
  font-size: 13px;
  font-weight: 500;
}

.rating-block-warning .material-symbols-outlined {
  font-size: 16px;
  color: var(--gold);
}

.rating-warn-fade-enter-active,
.rating-warn-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.rating-warn-fade-enter-from,
.rating-warn-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.rating-glow {
  animation: ratingGlow 0.8s ease 3;
  border-radius: 6px;
  padding: 4px 6px;
}

@keyframes ratingGlow {
  0%   { box-shadow: none; background: transparent; }
  50%  { box-shadow: 0 0 14px 4px rgba(201, 162, 39, 0.6); background: rgba(201, 162, 39, 0.08); }
  100% { box-shadow: none; background: transparent; }
}

.msg-rating {
  display: flex;
  align-items: center;
  gap: 4px;
}

.rating-has-comment {
  margin-left: 4px;
  display: flex;
  align-items: center;
}

.rating-comment-box {
  margin-top: 8px;
}

.rating-comment-input {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  resize: none;
  outline: none;
  font-family: inherit;
  color: #374151;
  transition: border-color 0.15s;
}

.rating-comment-input:focus {
  border-color: var(--sage-green);
}

.rating-comment-actions {
  display: flex;
  gap: 6px;
  margin-top: 5px;
}

.rating-comment-btn {
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.rating-comment-btn.primary {
  background: var(--sage-green);
  color: #fff;
  border-color: var(--sage-green);
}

.rating-comment-btn.primary:hover {
  background: #145040;
}

.rating-comment-preview {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.1s;
}

.rating-comment-preview:hover {
  background: rgba(0,0,0,0.04);
}

.rating-star-btn {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  line-height: 1;
  border-radius: 4px;
  transition: transform 0.1s ease;
}

.rating-star-btn:hover {
  transform: scale(1.2);
}

.rating-star-btn .material-symbols-outlined {
  font-size: 20px;
  display: block;
  color: #9ca3af;
  transition: color 0.15s ease;
}
.rating-star-btn.filled .material-symbols-outlined {
  color: var(--gold);
}
.rating-has-comment .material-symbols-outlined {
  font-size: 16px;
  color: var(--sage-green);
}

@media (prefers-reduced-motion: reduce) {
  .rating-star-btn { transition: none; }
  .rating-star-btn:hover { transform: none; }
}

.msg-meta {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
  opacity: 0.6;
}
.msg-time { font-size: 11px; color: var(--sage-text-secondary); }
.msg-row.is-user .msg-time { color: rgba(255, 255, 255, 0.7); }

.bottom-anchor { height: 1px; }

/* INPUT BAR */
.chat-input-bar {
  z-index: 20;
  padding: 12px 16px 16px;
}
.input-inner { max-width: 860px; margin: 0 auto; }

.chat-input-wrapper {
  width: 100%;
  display: flex;
  align-items: center;
  background: var(--sage-bg-main);
  border-radius: 8px;
  padding: 6px 8px 6px 4px;
  border: 2px solid var(--sage-border);
  box-shadow: 0 4px 12px rgba(90, 98, 112, 0.06);
  transition: border-color 0.2s, box-shadow 0.2s;
}

.chat-input-wrapper:focus-within {
  border-color: var(--sage-primary);
  box-shadow: 0 4px 20px rgba(90, 98, 112, 0.12);
}

.char-counter {
  font-size: 11px;
  color: #999;
  text-align: right;
  padding: 2px 8px 0;
}
.char-counter.over-limit {
  color: #e53935;
  font-weight: 600;
}

/* Arama Kapsamı Seçenekleri */
.search-scope-options {
  display: flex;
  gap: 16px;
  margin-top: 10px;
  justify-content: center;
}

.scope-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(90, 98, 112, 0.08);
  transition: all 0.2s ease;
}

.scope-checkbox:hover {
  background: rgba(90, 98, 112, 0.15);
}

.scope-checkbox input[type="checkbox"] {
  accent-color: var(--sage-primary);
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.scope-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: var(--sage-text-primary);
}

.scope-label .material-symbols-outlined {
  font-size: 16px;
  color: var(--sage-primary);
}

/* Footer'daki daha kompakt versiyon */
.chat-input-bar .search-scope-options {
  justify-content: flex-start;
  margin-top: 8px;
  gap: 12px;
}

.chat-input-bar .scope-checkbox {
  padding: 4px 10px;
}

.chat-input-bar .scope-label {
  font-size: 12px;
}

.chat-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  padding: 12px 14px;
  color: var(--sage-text-primary);
  background: transparent;
}
.chat-input::placeholder { color: var(--sage-text-muted); }

.right-icons { display: flex; align-items: center; gap: 6px; }

.send-btn {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 2px solid var(--sage-accent);
  background: var(--sage-primary);
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}
.send-btn:hover:not(:disabled) {
  background: var(--sage-primary-dark);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(90, 98, 112, 0.35);
}
.send-btn:active { transform: scale(0.97); }
.send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.stop-btn {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  border: 2px solid #dc2626;
  background: #dc2626;
  color: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.2s ease, box-shadow 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse-stop 1.5s ease-in-out infinite;
}
.stop-btn .material-symbols-outlined {
  font-size: 22px;
}
.stop-btn:hover {
  background: #b91c1c;
  border-color: #b91c1c;
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.35);
}
.stop-btn:active { transform: scale(0.97); }

@keyframes pulse-stop {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
}

.input-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--sage-text-secondary);
  text-align: center;
}

.disclaimer {
  margin-top: 10px;
  font-size: 12px;
  color: var(--sage-text-muted);
  text-align: center;
  line-height: 1.4;
}
.disclaimer-link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--sage-green);
  text-decoration: underline;
  cursor: pointer;
}
.disclaimer-link:hover { color: var(--sage-green-dark); }

.model-selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  justify-content: center;
}

.model-label {
  font-size: 12px;
  color: var(--sage-text-muted);
  user-select: none;
}

.model-toggle {
  display: flex;
  gap: 4px;
}

.model-btn {
  padding: 3px 10px;
  font-size: 12px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  background: transparent;
  color: var(--sage-text-muted);
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1.5;
}

.model-btn:hover {
  border-color: var(--sage-green);
  color: var(--sage-green);
}

.model-btn.active {
  background: var(--sage-green);
  border-color: var(--sage-green);
  color: #fff;
  font-weight: 500;
}

/* EMPTY MODE */
.chat-input-bar.is-center {
  position: fixed;
  left: 0; right: 0;
  bottom: auto;
  top: 58%;
  transform: translateY(-50%);
  background: transparent;
}
.chat-input-bar.is-center::before { content: none; }

/* CHAT MODE: sticky bottom */
.chat-input-bar.is-sticky {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-input);
  background: transparent;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.chat-input-bar.is-sticky::before {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 80px;
  background: linear-gradient(to top, #faf9f7 70%, rgba(250, 249, 247, 0));
  pointer-events: none;
}

.chat-input-bar.is-sticky .input-inner {
  position: relative;
  z-index: 1;
}

/* Typing animation - Legal Sage style */
/* Loading scales animation */
.loading-logo-bubble {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 24px !important;
}
.loading-scales {
  display: flex;
  align-items: center;
  justify-content: center;
}
.scales-svg {
  color: var(--sage-primary, var(--sage-green));
}
.scales-beam {
  transform-origin: 22px 14px;
  animation: scales-rock 2.2s ease-in-out infinite;
}
@keyframes scales-rock {
  0%   { transform: rotate(0deg); }
  25%  { transform: rotate(-9deg); }
  75%  { transform: rotate(9deg); }
  100% { transform: rotate(0deg); }
}
@media (prefers-reduced-motion: reduce) {
  .scales-beam { animation: none; }
  .rating-glow { animation: none !important; }
}
.loading-logo-text {
  margin-top: 10px;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
  white-space: nowrap;
}

/* Mobile */
@media (max-width: 767px) {
  .chat-title { font-size: 22px; }
  .msg-bubble { max-width: 94%; padding: 14px 14px; }
  .chat-input-bar.is-center { top: 62%; }
  .hero-input { margin-top: 16px; }
  .chat-body { padding: 16px 12px 130px; }
}

/* Sticky input sidebar offset */
.chat-input-wrapper.is-sticky > * {
  width: 100%;
  max-width: 760px;
}

@media (min-width: 768px) {
  .chat-input-bar.is-sticky {
    left: 260px;
    right: 0;
  }
  .mahai-sidebar.active ~ .chat-page .chat-input-bar.is-sticky {
    left: 60px;
  }
}

@media (max-width: 767px) {
  .chat-input-bar.is-sticky {
    left: 0 !important;
    right: 0 !important;
  }
}

/* Scroll down button */
.scroll-down-btn {
  position: fixed;
  right: 22px;
  bottom: 100px;
  z-index: var(--z-floating);
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: 2px solid var(--sage-border);
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(90, 98, 112, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s;
}
.scroll-down-btn:hover {
  box-shadow: 0 6px 20px rgba(90, 98, 112, 0.15);
  border-color: var(--sage-primary);
  transform: translateY(-1px);
}
.scroll-down-btn:active { transform: scale(0.96); }
.scroll-down-btn .material-symbols-outlined {
  font-size: 20px;
  color: var(--sage-primary);
}

@media (max-width: 767px) {
  .scroll-down-btn {
    right: 14px;
    bottom: 96px;
  }
}

/* Guide help button */
.guide-help-btn {
  position: fixed;
  right: 22px;
  bottom: 150px;
  z-index: var(--z-floating);
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: 2px solid var(--sage-accent);
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(201, 162, 39, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s;
}
.guide-help-btn:hover {
  background: var(--sage-accent);
  box-shadow: 0 6px 20px rgba(201, 162, 39, 0.30);
  transform: translateY(-1px);
}
.guide-help-btn:hover .material-symbols-outlined { color: #ffffff; }
.guide-help-btn:active { transform: scale(0.96); }
.guide-help-btn .material-symbols-outlined {
  font-size: 20px;
  color: var(--sage-accent);
  transition: color 0.12s;
}
@media (max-width: 767px) {
  .guide-help-btn { right: 14px; bottom: 146px; }
}

/* Modals */
.decisions-ai-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(28, 37, 32, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
}

.decisions-ai-modal {
  width: min(380px, 90vw);
  background: var(--sage-bg-main);
  border-radius: 12px;
  border: 1px solid var(--sage-border);
  border-left: 4px solid var(--sage-primary);
  box-shadow: 0 20px 50px rgba(90, 98, 112, 0.15);
  padding: 24px 22px 20px;
}

.decisions-ai-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--sage-text-primary);
  margin-bottom: 8px;
  font-family: 'Georgia', 'Times New Roman', serif;
}

.decisions-ai-modal-text {
  font-size: 14px;
  color: var(--sage-text-secondary);
  margin-bottom: 18px;
  line-height: 1.5;
}

.decisions-ai-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.decisions-ai-modal-btn {
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}
.decisions-ai-modal-btn:hover {
  transform: translateY(-1px);
}

.decisions-ai-modal-btn.ghost {
  background: var(--sage-bg-secondary);
  color: var(--sage-text-secondary);
  border: 1px solid var(--sage-border);
}

.decisions-ai-modal-btn.danger {
  background: rgba(196, 92, 74, 0.1);
  color: #c45c4a;
}

/* Principles modal */
.principles-modal {
  width: min(680px, 94vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.decisions-ai-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px;
  border-bottom: 1px solid var(--sage-border);
  background: var(--sage-bg-secondary);
  flex-shrink: 0;
}

.decisions-ai-modal-header h3 {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--sage-primary);
  font-family: 'Georgia', 'Times New Roman', serif;
}

.decisions-ai-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--sage-text-muted);
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 6px;
  transition: background 0.15s;
}

.decisions-ai-modal-close:hover {
  background: var(--sage-border);
}

.principles-modal-body {
  overflow-y: auto;
  padding: 22px 24px 28px;
}

.principles-modal-body .principles-intro {
  font-size: 14px;
  line-height: 1.75;
  color: var(--sage-text-secondary);
  margin: 0 0 20px 0;
  padding: 14px 16px;
  background: #fff;
  border-left: 4px solid var(--sage-primary);
  border: 1px solid var(--sage-border);
  border-left: 4px solid var(--sage-primary);
  border-radius: 0 8px 8px 0;
}

.principles-modal-body .principles-list {
  margin: 0 0 24px 0;
  padding: 0;
  list-style: none;
  counter-reset: principles-counter;
}

.principles-modal-body .principles-list > li {
  counter-increment: principles-counter;
  position: relative;
  padding: 14px 16px 14px 54px;
  margin-bottom: 8px;
  background: #fff;
  border: 1px solid var(--sage-border);
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--sage-text-secondary);
}

.principles-modal-body .principles-list > li::before {
  content: counter(principles-counter);
  position: absolute;
  left: 14px;
  top: 14px;
  width: 26px;
  height: 26px;
  background: var(--sage-primary);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}

.principles-modal-body .principles-list > li > ul {
  margin: 10px 0 0 0;
  padding-left: 18px;
}

.principles-modal-body .principles-list > li > ul > li {
  font-size: 13px;
  line-height: 1.65;
  color: var(--sage-text-muted);
  margin-bottom: 4px;
  list-style: disc;
}

.principles-modal-body .principles-signature {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 0 0;
  border-top: 2px solid var(--sage-border);
  color: var(--sage-primary);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
}

/* Shared banner */
.shared-banner {
  background: rgba(90, 98, 112, 0.05);
  border: 1px solid rgba(90, 98, 112, 0.15);
  border-left: 4px solid var(--sage-primary);
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--sage-text-primary);
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.shared-banner strong {
  color: var(--sage-primary);
  font-weight: 700;
}

.shared-banner .shared-banner-btn {
  padding: 8px 14px;
  border-radius: 20px;
  border: 2px solid var(--sage-accent);
  background: var(--sage-primary);
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: 0.15s ease;
}

.shared-banner .shared-banner-btn:hover {
  box-shadow: 0 4px 12px rgba(90, 98, 112, 0.3);
  transform: translateY(-1px);
  background: var(--sage-primary-dark);
}

/* Message actions (copy + like/dislike) */
.msg-actions {
  position: absolute;
  right: 12px;
  top: 12px;
  display: inline-flex;
  gap: 4px;
  opacity: 0;
  transform: translateY(-4px);
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.msg-bubble:hover .msg-actions {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.msg-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid var(--sage-border);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.12s ease, transform 0.08s ease, border-color 0.12s;
}

.msg-action-btn:hover {
  background: rgba(201, 162, 39, 0.1);
  border-color: var(--sage-accent);
}

.msg-action-btn:active {
  transform: scale(0.95);
}

.msg-action-btn .material-symbols-outlined {
  font-size: 16px;
  line-height: 1;
  color: var(--sage-text-secondary);
}

/* User message action buttons */
.msg-row.is-user .msg-action-btn {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
}
.msg-row.is-user .msg-action-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: var(--sage-accent);
}
.msg-row.is-user .msg-action-btn .material-symbols-outlined {
  color: rgba(255, 255, 255, 0.9);
}

.star-rating {
  display: flex;
  gap: 2px;
}
.star-btn .material-symbols-outlined {
  font-size: 18px;
}

/* ===========================
   ATIF UYARI SATIRI (doğrulanamayan atıf)
   =========================== */
.citation-warn-line {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 5px 12px;
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  font-weight: 500;
  font-family: var(--font-sans);
  background: var(--warn-soft);
  color: var(--warn);
  border: 1px solid rgba(180, 83, 9, 0.3);
}
.citation-warn-line .material-symbols-outlined {
  font-size: 16px;
}

/* ===========================
   DAYANAK BELGELER — Karar Künyesi kartları
   Gerçek bir Yargıtay kararının künye başlığı dilinde:
   mahkeme eyebrow'u, mono numaralar, altın doğrulama mührü.
   =========================== */
.msg-sources {
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid var(--rule);
}

.sources-header {
  font-family: var(--font-sans);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-secondary);
  margin-bottom: 12px;
}

.sources-group {
  margin-bottom: 14px;
}
.sources-group:last-child {
  margin-bottom: 0;
}

.sources-subheader {
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  margin: 6px 0;
}

.kunye-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;
}

.kunye-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: var(--paper-raised);
  border: 1px solid var(--rule);
  border-top: 2px solid var(--sage);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-card);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  animation: kunye-reveal 0.4s ease both;
}
/* Kartlar sırayla belirir — tek orkestre an */
.kunye-card:nth-child(2) { animation-delay: 0.07s; }
.kunye-card:nth-child(3) { animation-delay: 0.14s; }
.kunye-card:nth-child(4) { animation-delay: 0.21s; }
.kunye-card:nth-child(n+5) { animation-delay: 0.28s; }

@keyframes kunye-reveal {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
}

.kunye-card:hover {
  box-shadow: var(--shadow-raised);
  transform: translateY(-1px);
}

.kunye-card.is-unavailable {
  opacity: 0.6;
  border-top-color: var(--ink-faint);
}

/* Altın doğrulama mührü */
.kunye-seal {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}
.kunye-seal.seal-ok {
  background: var(--gold-soft);
  border: 1.5px solid var(--gold);
  color: #8a6d14;
  animation: seal-stamp 0.35s cubic-bezier(0.2, 1.4, 0.4, 1) 0.3s both;
}
.kunye-seal.seal-warn {
  background: var(--warn-soft);
  border: 1.5px solid var(--warn);
  color: var(--warn);
}
.kunye-seal .material-symbols-outlined {
  font-size: 17px;
}

@keyframes seal-stamp {
  from { opacity: 0; transform: scale(1.6); }
  to   { opacity: 1; transform: scale(1); }
}

.kunye-info {
  flex: 1;
  min-width: 0;
}

.kunye-court {
  font-family: var(--font-sans);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--sage);
  margin-bottom: 2px;
}

.kunye-numbers {
  font-family: var(--font-mono);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink);
  margin-bottom: 3px;
  word-break: break-word;
}

.kunye-name {
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-secondary);
  word-break: break-word;
  line-height: 1.4;
}

.source-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 0 7px;
  font-size: 10.5px;
  background: var(--sage-soft);
  color: var(--sage);
  border-radius: 9px;
  font-weight: 500;
  font-family: var(--font-sans);
}

.kunye-actions {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.source-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid var(--rule);
  background: var(--paper-raised);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.12s ease;
}

.source-btn:hover:not(:disabled) {
  background: var(--sage-soft);
  border-color: var(--sage);
}

.source-btn:focus-visible {
  outline: 2px solid var(--sage);
  outline-offset: 1px;
}

.source-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.source-btn .material-symbols-outlined {
  font-size: 17px;
  color: var(--ink-secondary);
}

.source-btn:hover:not(:disabled) .material-symbols-outlined {
  color: var(--sage);
}

@media (max-width: 600px) {
  .kunye-list {
    grid-template-columns: 1fr;
  }
  .kunye-actions {
    flex-direction: row;
    gap: 8px;
  }
  /* Dokunma hedefleri ≥40px */
  .source-btn {
    width: 40px;
    height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .kunye-card,
  .kunye-seal.seal-ok {
    animation: none !important;
  }
}

</style>
